/**
 * NetworkManager - WebRTC PeerJS Networking & Multiplayer State Synchronization
 * Handles room credential generation, canonical join URLs, state serialization,
 * opponent card sanitization / privacy masking, seat allocation, message routing,
 * and authoritative host intent processing.
 */

import { CameraManager } from './CameraManager.js';
import { ChatManager } from './ChatManager.js';
import { ReactionManager } from '../ui/ReactionManager.js';
import { AvatarManager } from '../ui/AvatarManager.js';
import { appStorage } from '../storage/IndexedDBStorage.js';
import { sanitizeUserInput } from '../utils/Sanitizer.js';

function getStorage(context = {}) {
  return context.storage || (typeof window !== 'undefined' && window.appStorage) || appStorage || (typeof localStorage !== 'undefined' ? localStorage : null);
}

export const ROOM_CODE_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

export const DEFAULT_PEER_CONFIG = {
  debug: 1,
  config: {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
      { urls: 'stun:stun2.l.google.com:19302' },
      { urls: 'stun:stun3.l.google.com:19302' },
      { urls: 'stun:stun4.l.google.com:19302' },
      { urls: 'stun:global.stun.twilio.com:3478' }
    ]
  }
};

export class NetworkManager {
  constructor(options = {}) {
    this.role = options.role || 'solo'; // 'host' | 'guest' | 'solo'
    this.room = options.room || null;
    this.mySeatId = options.mySeatId || 'p1';
    this.peer = null;
    this.conn = null;
    this.clients = new Map(); // peerId -> { conn, seatId, name }
    this.ready = false;
  }

  /**
   * Sanitizes and normalizes player names against HTML injection, control characters, and excessive length.
   * Strips HTML tags, metacharacters, and non-printable control characters.
   * @param {*} rawName
   * @param {string} [defaultFallback='Player']
   * @returns {string}
   */
  static sanitizePlayerName(rawName, defaultFallback = 'Player') {
    if (typeof rawName !== 'string') return defaultFallback;
    let cleaned = rawName
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // strip script blocks
      .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')   // strip style blocks
      .replace(/<[^>]*>/g, '') // strip HTML tags
      .replace(/[<>"'&`/\\;]/g, '') // strip HTML & script injection metacharacters
      .replace(/[\u0000-\u001F\u007F-\u009F]/g, '') // strip non-printable control characters
      .replace(/\s+/g, ' ') // collapse multiple spaces
      .trim();
    if (!cleaned) return defaultFallback;
    return cleaned.slice(0, 24);
  }

  /**
   * Generates a 6-character clean uppercase room code (excluding ambiguous 0/O, 1/I).
   * @param {number} [length=6]
   * @returns {string}
   */
  static generateRoomCode(length = 6) {
    let s = '';
    for (let i = 0; i < length; i++) {
      s += ROOM_CODE_CHARS[Math.floor(Math.random() * ROOM_CODE_CHARS.length)];
    }
    return s;
  }

  /**
   * Generates a canonical join URL for sharing with guests.
   * @param {string} code
   * @param {string} [origin]
   * @returns {string}
   */
  static getJoinUrl(code, origin = '') {
    const roomCode = (code || '').trim().toUpperCase();
    if (!origin && typeof window !== 'undefined' && window.location) {
      origin = window.location.href;
    }
    if (!origin) return roomCode ? `?room=${encodeURIComponent(roomCode)}` : '';
    try {
      const url = new URL(origin);
      let brand = url.searchParams.get('brand');
      if (!brand && typeof window !== 'undefined' && typeof window.getAppBrand === 'function') {
        brand = window.getAppBrand();
      }
      url.search = '';
      url.hash = '';
      if (roomCode) url.searchParams.set('room', roomCode);
      if (brand && brand !== 'crazy') url.searchParams.set('brand', brand);
      return url.toString();
    } catch (e) {
      const base = origin.split('?')[0].split('#')[0];
      return roomCode ? `${base}?room=${encodeURIComponent(roomCode)}` : base;
    }
  }

  /**
   * Strips internal non-serializable fields (e.g. DOM nodes, closures) from game state.
   * @param {Object} state
   * @returns {Object} Clean JSON-serializable state
   */
  static serializeState(state) {
    if (!state || typeof state !== 'object') return {};
    const skip = {
      pendingWild: true,
      _resumeAfterSign: true,
      _resumeAfterAdultTimer: true,
      _resumeAfterPintsBonus: true
    };
    const out = {};
    for (const k of Object.keys(state)) {
      if (!skip[k]) {
        out[k] = state[k];
      }
    }
    return out;
  }

  serializeState(state) {
    return NetworkManager.serializeState(state);
  }

  /**
   * Sanitizes game state before transmission to a client: masks opponent cards,
   * hides hidden crib cards during play, and strips undealt deck card identities.
   * @param {Object} rawState Full authoritative game state
   * @param {string} targetSeatId Seat identifier of the recipient (e.g. 'p2')
   * @returns {Object} Sanitized state safe for transmission
   */
  static sanitizeForClient(rawState, targetSeatId) {
    if (!rawState || typeof rawState !== 'object') return rawState;
    const out = { ...rawState };
    const isShowOrOver = out.phase === 'show' || out.phase === 'show-p2' || out.phase === 'show-multi' || Boolean(out.over);

    // Sanitize seatHands: only targetSeatId sees their own unplayed cards face-up
    if (out.seatHands && typeof out.seatHands === 'object') {
      const safeHands = {};
      for (const [sId, hand] of Object.entries(out.seatHands)) {
        if (!Array.isArray(hand)) {
          safeHands[sId] = hand;
          continue;
        }
        if (sId === targetSeatId || isShowOrOver) {
          safeHands[sId] = hand.map(c => ({ ...c }));
        } else {
          safeHands[sId] = hand.map(c => ({
            id: c.id,
            rank: '?',
            suit: '?',
            val: 0,
            masked: true
          }));
        }
      }
      out.seatHands = safeHands;
    }

    // Sanitize 2-player legacy youHand / cpuHand
    if (targetSeatId === 'p2') {
      if (out.youHand && Array.isArray(out.youHand) && !isShowOrOver) {
        out.youHand = out.youHand.map(c => ({ id: c.id, rank: '?', suit: '?', val: 0, masked: true }));
      }
    } else if (targetSeatId === 'p1') {
      if (out.cpuHand && Array.isArray(out.cpuHand) && !isShowOrOver) {
        out.cpuHand = out.cpuHand.map(c => ({ id: c.id, rank: '?', suit: '?', val: 0, masked: true }));
      }
    }

    // Sanitize crib: only reveal if in show phase counting crib or game over
    if (out.crib && Array.isArray(out.crib)) {
      const isCountingCrib = (out.phase === 'show' || out.phase === 'show-multi') &&
        out.showQueue && out.showQueue[out.showStep] === 'crib';
      if (!isCountingCrib && !out.over) {
        out.crib = out.crib.map(c => ({ id: c.id, rank: '?', suit: '?', val: 0, masked: true }));
      }
    }

    // Never transmit undealt deck cards across the wire
    if (out.deck && Array.isArray(out.deck)) {
      out.deckCount = out.deck.length;
      out.deck = [];
    }

    return out;
  }

  sanitizeForClient(rawState, targetSeatId) {
    return NetworkManager.sanitizeForClient(rawState, targetSeatId);
  }

  /**
   * Computes seat assignment for a new connecting client.
   * @param {Iterable<Object>} existingClients Map values or array of existing clients
   * @param {number} [playerCount=2] Max table player count
   * @param {string} [connectingPeerId=null]
   * @returns {string|null} Available seat ID (e.g. 'p2'..'p8') or null if table is full
   */
  static allocateSeat(existingClients, playerCount = 2, connectingPeerId = null) {
    const taken = new Set();
    if (existingClients) {
      for (const client of existingClients) {
        if (client.seatId && (!connectingPeerId || client.peerId !== connectingPeerId)) {
          taken.add(client.seatId);
        }
      }
    }
    const maxCount = Math.max(2, Math.min(8, parseInt(playerCount, 10) || 2));
    for (let i = 2; i <= maxCount; i++) {
      const sId = 'p' + i;
      if (!taken.has(sId)) return sId;
    }
    return null;
  }

  allocateSeat(playerCount = 2, connectingPeerId = null) {
    return NetworkManager.allocateSeat(this.clients.values(), playerCount, connectingPeerId);
  }

  /**
   * Processes authoritative client intents received by the host.
   * @param {Object} msg Intent message { op, seatId, ... }
   * @param {Object} handlers Object with callback handlers for various operations
   * @returns {boolean} Whether the intent was successfully dispatched
   */
  static processClientIntent(msg, handlers = {}) {
    if (!msg || typeof msg !== 'object' || !msg.op) return false;
    const sId = msg.seatId || 'p2';

    switch (msg.op) {
      case 'play':
        if (typeof handlers.onPlay === 'function') {
          handlers.onPlay(sId, msg.cardId);
          return true;
        }
        break;
      case 'discard':
      case 'discardP2':
        if (typeof handlers.onDiscard === 'function') {
          handlers.onDiscard(sId, msg.ids || []);
          return true;
        }
        break;
      case 'cut':
        if (typeof handlers.onCut === 'function') {
          handlers.onCut(sId, msg.index);
          return true;
        }
        break;
      case 'cutStarter':
        if (msg.guessRank != null && handlers.state) {
          handlers.state.starterGuess = { who: sId, rank: Number(msg.guessRank) };
        }
        if (typeof handlers.onCutStarter === 'function') {
          handlers.onCutStarter(sId, msg.guessRank);
          return true;
        }
        break;
      case 'stolenCribGuess':
        if (typeof handlers.onStolenCribGuess === 'function') {
          handlers.onStolenCribGuess(sId, msg.guess);
          return true;
        }
        break;
      case 'twistedCribbageSplit':
        if (typeof handlers.onTwistedCribbageSplit === 'function') {
          handlers.onTwistedCribbageSplit(sId, msg.hand1, msg.hand2);
          return true;
        }
        break;
      case 'wild':
        if (typeof handlers.onWild === 'function') {
          handlers.onWild(sId, msg.cardId, msg.r, msg.s);
          return true;
        }
        break;
      case 'playAgain':
        if (typeof handlers.onPlayAgain === 'function') {
          handlers.onPlayAgain(sId);
          return true;
        }
        break;
    }
    return false;
  }

  processClientIntent(msg, handlers = {}) {
    return NetworkManager.processClientIntent(msg, handlers);
  }

  /**
   * Updates lobby roster DOM element and player counts.
   * @param {Array} [remoteRoster]
   * @param {Object} [context]
   */
  static updateNetRoster(remoteRoster, context = {}) {
    const doc = context.document || (typeof document !== 'undefined' ? document : null);
    if (!doc) return;
    const rosterWrap = doc.getElementById('netRoster');
    const listEl = doc.getElementById('netRosterList');
    const countEl = doc.getElementById('netRosterCount');
    const btnStartGame = doc.getElementById('btnStartOnlineGame');

    if (!rosterWrap || !listEl) return;

    const optOpp = doc.getElementById('optOpp');
    const isOnline = typeof context.isOnline === 'function' ? context.isOnline() : (typeof window !== 'undefined' && typeof window.isOnline === 'function' ? window.isOnline() : false);
    const isOnlineMode = isOnline || (optOpp && optOpp.value === 'online');
    if (!isOnlineMode) {
      rosterWrap.style.display = 'none';
      return;
    }

    rosterWrap.style.display = 'block';
    const getLobbyRosterData = context.getLobbyRosterData || (typeof window !== 'undefined' ? window.getLobbyRosterData : null);
    const roster = remoteRoster || (typeof getLobbyRosterData === 'function' ? getLobbyRosterData() : []);
    const net = context.net || (typeof window !== 'undefined' ? window.net : null);
    if (net) {
      net.roster = roster;
    }

    const totalSeats = roster.length;
    const humanCount = roster.filter(r => r.type === 'human' || r.status === 'host' || r.status === 'online').length;
    if (countEl) {
      countEl.textContent = `Players: ${humanCount} / ${totalSeats}`;
    }

    listEl.innerHTML = roster.map(r => {
      let dotClass = 'waiting';
      let dotLabel = r.statusText || 'Waiting';
      if (r.status === 'host' || r.status === 'online') {
        dotClass = 'online';
      } else if (r.status === 'cpu') {
        dotClass = 'cpu';
      }
      let avatarBadgeHtml = '';
      const avMgr = context.AvatarManager || (typeof window !== 'undefined' && window.AvatarManager) || AvatarManager || null;
      if (avMgr && typeof avMgr.renderAvatarBadge === 'function') {
        let targetAvId = null;
        if (r.isYou) {
          targetAvId = typeof avMgr.getSelectedAvatarId === 'function' ? avMgr.getSelectedAvatarId() : 'jack';
        } else {
          const settingsObj = context.settings || (typeof window !== 'undefined' ? window.settings : null);
          const seatObj = settingsObj && Array.isArray(settingsObj.seats) ? settingsObj.seats.find(s => s && s.id === r.id) : null;
          const av = seatObj?.avatar || r.avatar || r.avatarId;
          if (av === 'custom') {
            targetAvId = 'custom_' + r.id;
          } else {
            targetAvId = av || (r.type === 'cpu' ? 'robot' : 'jack');
          }
        }
        if (targetAvId && avMgr.getAvatarById(targetAvId)) {
          avatarBadgeHtml = `<span class="net-roster-avatar" style="margin-right:6px;vertical-align:middle;display:inline-flex;">${avMgr.renderAvatarBadge(targetAvId, { size: 22, showRing: false })}</span>`;
        }
      }
      return `
        <div class="net-roster-row ${r.isYou ? 'you' : ''}">
          <div class="net-roster-seat">P${r.num}</div>
          <div class="net-roster-name" style="flex: 1; font-weight: ${r.isYou ? 'bold' : 'normal'}; display:flex; align-items:center;">
            ${avatarBadgeHtml}<span>${NetworkManager.sanitizePlayerName(r.name, `Player ${r.num}`)}</span> ${r.isYou ? '<span style="font-size:0.8em;opacity:0.75;margin-left:4px;">(You)</span>' : ''}
          </div>
          <div class="net-roster-tag">
            <span class="net-status-dot ${dotClass}"></span>
            <span>${dotLabel}</span>
          </div>
        </div>
      `;
    }).join('');

    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
    if (btnStartGame) {
      if (settings && settings.netRole === 'host') {
        btnStartGame.style.display = 'inline-flex';
      } else {
        btnStartGame.style.display = 'none';
      }
    }
  }

  /**
   * Broadcasts active lobby state & roster to all connected guests.
   * @param {Object} [context]
   */
  static broadcastLobbyUpdate(context = {}) {
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
    if (!settings || settings.netRole !== 'host') return;
    const net = context.net || (typeof window !== 'undefined' ? window.net : null);
    if (!net || !net.clients) return;

    const getLobbyRosterData = context.getLobbyRosterData || (typeof window !== 'undefined' ? window.getLobbyRosterData : null);
    const roster = typeof getLobbyRosterData === 'function' ? getLobbyRosterData() : (net.roster || []);
    const onlineSeats = ['p1'];
    if (net.clients) {
      for (const client of net.clients.values()) {
        if (client && client.seatId && client.conn && client.conn.open) {
          onlineSeats.push(client.seatId);
        }
      }
    }
    const payload = {
      type: 'lobby',
      roster,
      playerCount: settings.playerCount,
      seats: settings.seats,
      onlineSeats: Array.from(new Set(onlineSeats))
    };
    for (const client of net.clients.values()) {
      if (client.conn && client.conn.open) {
        try { client.conn.send(payload); } catch (e) { console.warn('Lobby broadcast err:', e); }
      }
    }
  }

  /**
   * Authoritative / guest network send helper.
   * @param {Object} msg
   * @param {Object} [targetConn]
   * @param {Object} [context]
   */
  static netSend(msg, targetConn, context = {}) {
    if (targetConn && targetConn.open) {
      try { targetConn.send(msg); } catch (e) { console.warn('netSend error:', e); }
      return;
    }
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
    const net = context.net || (typeof window !== 'undefined' ? window.net : null);
    if (!net) return;

    if (settings && settings.netRole === 'host') {
      if (net.clients) {
        for (const client of net.clients.values()) {
          if (client.conn && client.conn.open) {
            try { client.conn.send(msg); } catch (e) { console.warn('netSend broadcast error:', e); }
          }
        }
      }
    } else if (net.conn && net.conn.open) {
      try { net.conn.send(msg); } catch (e) { console.warn('netSend guest error:', e); }
    }
  }

  /**
   * Pushes sanitized state snapshots from host to all connected clients.
   * @param {Object} [extra]
   * @param {Object} [context]
   */
  static netPush(extra = {}, context = {}) {
    const isOnline = typeof context.isOnline === 'function' ? context.isOnline() : (typeof window !== 'undefined' && typeof window.isOnline === 'function' ? window.isOnline() : false);
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
    if (!isOnline || !settings || settings.netRole !== 'host') return;

    const net = context.net || (typeof window !== 'undefined' ? window.net : null);
    if (!net || !net.clients) return;

    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    const serializeState = context.serializeState || (typeof window !== 'undefined' && typeof window.serializeState === 'function' ? window.serializeState : NetworkManager.serializeState);
    const getSanitizedStateForSeat = context.getSanitizedStateForSeat || (typeof window !== 'undefined' && typeof window.getSanitizedStateForSeat === 'function' ? window.getSanitizedStateForSeat : (sId, raw) => NetworkManager.sanitizeForClient(raw, sId));

    const onlineSeats = ['p1'];
    if (net.clients) {
      for (const client of net.clients.values()) {
        if (client && client.seatId && client.conn && client.conn.open) {
          onlineSeats.push(client.seatId);
        }
      }
    }
    const uniqueOnlineSeats = Array.from(new Set(onlineSeats));

    const rawState = serializeState(state);
    for (const [peerId, client] of net.clients.entries()) {
      if (client.conn && client.conn.open) {
        const sanitized = getSanitizedStateForSeat(client.seatId, rawState);
        try {
          client.conn.send({
            type: 'state',
            state: sanitized,
            settings: { ...settings },
            mySeatId: client.seatId,
            onlineSeats: uniqueOnlineSeats,
            ...extra
          });
        } catch (e) {
          console.warn('netPush error to', peerId, e);
        }
      }
    }
  }

  /**
   * Applies remote game state snapshot received from host to client view.
   * @param {Object} payload
   * @param {Object} [context]
   */
  static applyRemoteState(payload, context = {}) {
    if (!payload || !payload.state) return;
    const hideWait = context.hideWait || (typeof window !== 'undefined' ? window.hideWait : () => {});
    hideWait();

    const net = context.net || (typeof window !== 'undefined' ? window.net : null);
    if (net && payload.mySeatId) {
      net.mySeatId = payload.mySeatId;
    }

    if (context.setState && typeof context.setState === 'function') {
      context.setState(payload.state);
    } else if (typeof window !== 'undefined') {
      window.state = payload.state;
    }

    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
    if (settings) {
      if (payload.settings) Object.assign(settings, payload.settings);
      settings.opp = 'online';
      settings.netRole = 'guest';
    }

    if (Array.isArray(payload.onlineSeats)) {
      if (net) net.onlineSeats = payload.onlineSeats;
      if (settings && settings.seats) {
        settings.seats.forEach(s => {
          if (s && s.id) {
            s.online = payload.onlineSeats.includes(s.id);
          }
        });
      }
    }

    const doc = context.document || (typeof document !== 'undefined' ? document : null);
    if (doc) {
      const menuEl = doc.getElementById('menu');
      const gameEl = doc.getElementById('game');
      if (menuEl) menuEl.classList.add('hidden');
      if (gameEl) gameEl.classList.remove('hidden');

      const getBoardSignature = context.getBoardSignature || (typeof window !== 'undefined' ? window.getBoardSignature : null);
      const buildBoard = context.buildBoard || (typeof window !== 'undefined' ? window.buildBoard : null);
      if (typeof getBoardSignature === 'function' && typeof buildBoard === 'function') {
        const targetSig = getBoardSignature();
        const currentSig = typeof window !== 'undefined' ? window._currentBoardSignature : null;
        if (currentSig !== targetSig || !doc.getElementById('serpentineBoard')) {
          buildBoard();
        }
      }

      const applySeatLabels = context.applySeatLabels || (typeof window !== 'undefined' ? window.applySeatLabels : null);
      if (typeof applySeatLabels === 'function') applySeatLabels();

      const renderScores = context.renderScores || (typeof window !== 'undefined' ? window.renderScores : null);
      if (typeof renderScores === 'function') renderScores(true);

      const render = context.render || (typeof window !== 'undefined' ? window.render : null);
      if (typeof render === 'function') render();

      const fancyCall = context.fancyCall || (typeof window !== 'undefined' ? window.fancyCall : null);
      if (payload.fancy && typeof fancyCall === 'function') {
        let fText = payload.fancy.text;
        if (payload.fancy.kind === 'go' && (payload.fancy.goSeat || payload.fancy.goSeats)) {
          const mySeat = context.mySeat || (typeof window !== 'undefined' && typeof window.mySeat === 'function' ? window.mySeat : () => (net ? net.mySeatId : 'p2'));
          const seatName = context.seatName || (typeof window !== 'undefined' && typeof window.seatName === 'function' ? window.seatName : s => s);
          const me = mySeat();
          const seats = payload.fancy.goSeats || [payload.fancy.goSeat];
          const names = seats.map(s => {
            if (s === me) return 'You';
            const n = seatName(s);
            return (n === 'You' || (typeof n === 'string' && n.endsWith('(You)'))) ? 'You' : n;
          });
          let whoSays = '';
          if (names.length === 1) {
            whoSays = (names[0] === 'You') ? 'You say "Go!"' : `${names[0]} says "Go!"`;
          } else if (names.length === 2) {
            whoSays = `${names[0]} & ${names[1]} say "Go!"`;
          } else {
            whoSays = `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]} say "Go!"`;
          }
          if (payload.fancy.scorerSeat) {
            const sc = payload.fancy.scorerSeat === me ? 'You' : seatName(payload.fancy.scorerSeat);
            fText = `${whoSays} — 1 pt to ${sc}`;
          } else {
            fText = whoSays;
          }

          try {
            const b3d = typeof window !== 'undefined' ? (window.boardRenderer3D || window._currentRenderer3D) : null;
            if (b3d && typeof b3d.showSeatReaction === 'function') {
              seats.forEach(s => b3d.showSeatReaction(s, '💬 GO!'));
            }
          } catch (_) {}
        } else if (payload.fancy.kind === 'play-turn' && payload.fancy.turnSeat) {
          const mySeat = context.mySeat || (typeof window !== 'undefined' && typeof window.mySeat === 'function' ? window.mySeat : () => (net ? net.mySeatId : 'p2'));
          const seatName = context.seatName || (typeof window !== 'undefined' && typeof window.seatName === 'function' ? window.seatName : s => s);
          const me = mySeat();
          const turnSeat = payload.fancy.turnSeat;
          fText = (turnSeat === me)
            ? 'Your turn: Play a card'
            : `${seatName(turnSeat)}’s turn: Play a card`;

          try {
            const b3d = typeof window !== 'undefined' ? (window.boardRenderer3D || window._currentRenderer3D) : null;
            if (b3d && typeof b3d.showSeatReaction === 'function') {
              b3d.showSeatReaction(turnSeat, '🎴');
            }
          } catch (_) {}
        }
        fancyCall(fText, payload.fancy.kind, payload.fancy.ms);
      }

      const sfx = context.sfx || (typeof window !== 'undefined' ? window.sfx : null);
      if (payload.sfx && sfx && typeof sfx[payload.sfx] === 'function') {
        sfx[payload.sfx]();
      }

      const log = context.log || (typeof window !== 'undefined' ? window.log : null);
      if (payload.log && typeof log === 'function') {
        log(payload.log);
      }

      const setShowMeter = context.setShowMeter || (typeof window !== 'undefined' ? window.setShowMeter : null);
      if (payload.showMeter && typeof setShowMeter === 'function') {
        setShowMeter(payload.showMeter.act, payload.showMeter.total, payload.showMeter.line);
      }

      const openShowModal = context.openShowModal || (typeof window !== 'undefined' ? window.openShowModal : null);
      const hideModal = context.hideModal || (typeof window !== 'undefined' ? window.hideModal : null);
      const currentState = payload.state;
      if (payload.modal === 'show' || (currentState && currentState.showModal && currentState.showModal.open)) {
        if (typeof openShowModal === 'function') {
          openShowModal(payload.pegOnly != null ? payload.pegOnly : (currentState.showModal && currentState.showModal.pegOnly));
        }
      } else if (payload.modal === 'hide' || (currentState && !currentState.showModal && doc.getElementById('modal') && doc.getElementById('modal').querySelector('#btnNextHand, #btnGuestWait'))) {
        if (typeof hideModal === 'function') hideModal();
      }
    }
  }

  /**
   * Processes discard intention from a human seat on the host.
   * @param {string} sId
   * @param {Array<number|string>} ids
   * @param {Object} [context]
   */
  static handleSeatDiscardIntent(sId, ids, context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
    if (!state || !settings || (state.phase !== 'discard' && state.phase !== 'discard-p2')) return;

    const count = settings.playerCount || 2;
    const is7Card = Boolean(settings && settings.opt7CardCribbage && count === 2);
    let discardN = count >= 3 ? 1 : (is7Card ? 2 : (settings.deal === 'super' ? 3 : 2));
    if ((settings.optSpeedoCribbage || settings.optTwistedCribbage) && count === 2) {
      discardN = (sId === state.dealer) ? (settings.deal === 'super' ? 6 : 4) : (settings.deal === 'super' ? 3 : 2);
    }
    if (!Array.isArray(ids) || ids.length !== discardN) return;

    const getSeatHand = context.getSeatHand || (typeof window !== 'undefined' ? window.getSeatHand : () => []);
    const setSeatHand = context.setSeatHand || (typeof window !== 'undefined' ? window.setSeatHand : () => {});
    const setSeatKept = context.setSeatKept || (typeof window !== 'undefined' ? window.setSeatKept : () => {});
    const seatDiscardsToDeck = context.seatDiscardsToDeck || (typeof window !== 'undefined' ? window.seatDiscardsToDeck : () => false);
    const seatName = context.seatName || (typeof window !== 'undefined' ? window.seatName : s => s);
    const mySeat = context.mySeat || (typeof window !== 'undefined' ? window.mySeat : () => 'p1');
    const getActiveSeatIds = context.getActiveSeatIds || (typeof window !== 'undefined' ? window.getActiveSeatIds : () => []);
    const isSeatHuman = context.isSeatHuman || (typeof window !== 'undefined' ? window.isSeatHuman : () => false);
    const isSeatCpu = context.isSeatCpu || (typeof window !== 'undefined' ? window.isSeatCpu : () => false);
    const isDealerTeam = context.isDealerTeam || (typeof window !== 'undefined' ? window.isDealerTeam : () => false);
    const chooseDiscard = context.chooseDiscard || (typeof window !== 'undefined' ? window.chooseDiscard : () => []);
    const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});
    const log = context.log || (typeof window !== 'undefined' ? window.log : () => {});
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});
    const netPush = context.netPush || (typeof window !== 'undefined' ? window.netPush : NetworkManager.netPush);
    const startPlayAfterDiscards = context.startPlayAfterDiscards || (typeof window !== 'undefined' ? window.startPlayAfterDiscards : () => {});
    const startStarter = context.startStarter || (typeof window !== 'undefined' ? window.startStarter : () => {});
    const beginCutStarter = context.beginCutStarter || (typeof window !== 'undefined' ? window.beginCutStarter : null);

    const idSet = new Set(ids);
    const currHand = getSeatHand(sId);
    const thrown = currHand.filter(c => idSet.has(c.id));
    if (thrown.length !== discardN) return;
    if (settings && settings.optFivesCribbage && !seatDiscardsToDeck(sId)) {
      const fivesInHand = currHand.filter(c => ((c.rank !== undefined ? c.rank : c.r) || 0) === 5);
      const reqFives = Math.min(fivesInHand.length, discardN);
      const thrownFives = thrown.filter(c => ((c.rank !== undefined ? c.rank : c.r) || 0) === 5);
      if (thrownFives.length < reqFives) return;
    }
    if (settings && settings.optSevensAndEightsCribbage && !seatDiscardsToDeck(sId)) {
      const sevensAndEightsInHand = currHand.filter(c => {
        const r = ((c.rank !== undefined ? c.rank : c.r) || 0);
        return r === 7 || r === 8;
      });
      const reqCount = Math.min(sevensAndEightsInHand.length, discardN);
      const thrownTarget = thrown.filter(c => {
        const r = ((c.rank !== undefined ? c.rank : c.r) || 0);
        return r === 7 || r === 8;
      });
      if (thrownTarget.length < reqCount) return;
    }

    const remaining = currHand.filter(c => !idSet.has(c.id));
    setSeatHand(sId, remaining);
    setSeatKept(sId, remaining.slice());

    state.seatDiscards = state.seatDiscards || {};
    state.seatDiscards[sId] = thrown;

    if ((settings.optSpeedoCribbage || settings.optTwistedCribbage) && count === 2) {
      if (sId !== state.dealer) {
        const dHand = (getSeatHand(state.dealer) || []).slice();
        dHand.push(...thrown);
        setSeatHand(state.dealer, dHand);
        log(`${seatName(sId)} discarded ${thrown.length} card${thrown.length > 1 ? 's' : ''} to ${seatName(state.dealer)}'s hand.`);
      } else {
        state.crib = state.crib || [];
        state.crib.push(...thrown);
        log(`${seatName(sId)} discarded ${thrown.length} cards to the crib.`);
      }
    } else if (seatDiscardsToDeck(sId)) {
      state.deckDiscards = state.deckDiscards || [];
      state.deckDiscards.push(...thrown);
      log(`${seatName(sId)} discards 1 card to the deck.`);
    } else {
      state.crib = state.crib || [];
      state.crib.push(...thrown);
    }

    if (sId === mySeat()) {
      state.selected = [];
    }

    const activeSeats = getActiveSeatIds();
    const pendingHumans = activeSeats.filter(id => isSeatHuman(id) && (!state.seatDiscards || !state.seatDiscards[id]));

    if (pendingHumans.length > 0) {
      const pendingNames = pendingHumans.map(id => seatName(id)).join(', ');
      setStatus(`Waiting for ${pendingNames} to discard…`);
      render();
      netPush();
      return;
    }

    for (const seat of activeSeats) {
      if (isSeatCpu(seat) && (!state.seatDiscards || !state.seatDiscards[seat])) {
        const sHand = getSeatHand(seat);
        const isDealer = isDealerTeam(seat);
        const toDeck = seatDiscardsToDeck(seat);
        const cpuDiscardN = ((settings.optSpeedoCribbage || settings.optTwistedCribbage) && count === 2 && seat === state.dealer)
          ? (settings.deal === 'super' ? 6 : 4)
          : discardN;
        const cpuThrow = chooseDiscard(sHand, isDealer, settings.goal === 'low', cpuDiscardN, toDeck);
        const cids = new Set(cpuThrow.map(c => c.id));
        const sKept = sHand.filter(c => !cids.has(c.id));
        setSeatHand(seat, sKept);
        setSeatKept(seat, sKept.slice());
        state.seatDiscards[seat] = cpuThrow;
        if (toDeck) {
          state.deckDiscards = state.deckDiscards || [];
          state.deckDiscards.push(...cpuThrow);
          log(`${seatName(seat)} discards 1 card to the deck.`);
        } else {
          state.crib = state.crib || [];
          state.crib.push(...cpuThrow);
        }
      }
    }

    state.currentHumanDiscard = null;
    if (settings.optCutBeforeDiscard) {
      state.discardsComplete = true;
      startPlayAfterDiscards();
    } else if (settings.optGuessStarter) {
      state.discardsComplete = true;
      const beginCutStarter = context.beginCutStarter || (typeof window !== 'undefined' ? window.beginCutStarter : null);
      if (typeof beginCutStarter === 'function') beginCutStarter();
      else startStarter();
    } else {
      startStarter();
    }
    render();
    netPush();
  }

  /**
   * Applies client game intents on the host machine.
   * @param {Object} msg
   * @param {Object} [fromConn]
   * @param {Object} [context]
   */
  static hostApplyIntent(msg, fromConn, context = {}) {
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
    if (!msg || !settings || settings.netRole !== 'host') return;
    const sId = msg.seatId || (fromConn && fromConn._seatId) || 'p2';
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    const netPush = context.netPush || (typeof window !== 'undefined' ? window.netPush : NetworkManager.netPush);

    if (msg.op === 'stolenCribGuess') {
      const resolveStolenCribGuess = context.resolveStolenCribGuess || (typeof window !== 'undefined' ? window.resolveStolenCribGuess : null);
      if (typeof resolveStolenCribGuess === 'function') {
        resolveStolenCribGuess(msg.guess, state && state.showQueue ? state.showQueue.indexOf('crib') : -1);
      }
      netPush();
      return;
    }
    if (msg.op === 'twistedCribbageSplit') {
      const resolveTwistedCribbageSplit = context.resolveTwistedCribbageSplit || (typeof window !== 'undefined' ? window.resolveTwistedCribbageSplit : null);
      if (typeof resolveTwistedCribbageSplit === 'function') {
        const dealerIdx = state && state.showQueue ? state.showQueue.indexOf('dealer') : 1;
        resolveTwistedCribbageSplit(msg.hand1, msg.hand2, dealerIdx);
      }
      netPush();
      return;
    }
    if (msg.op === 'cut') {
      const pickCut = context.pickCut || (typeof window !== 'undefined' ? window.pickCut : null);
      if (typeof pickCut === 'function') pickCut(msg.index, sId);
      return;
    }
    if (msg.op === 'cutStarter') {
      if (msg.guessRank != null && state) {
        state.starterGuess = { who: sId, rank: Number(msg.guessRank) };
      }
      const poneDoCutStarter = context.poneDoCutStarter || (typeof window !== 'undefined' ? window.poneDoCutStarter : null);
      if (typeof poneDoCutStarter === 'function') poneDoCutStarter();
      return;
    }
    if (msg.op === 'play') {
      const getSeatHand = context.getSeatHand || (typeof window !== 'undefined' ? window.getSeatHand : () => []);
      const playCard = context.playCard || (typeof window !== 'undefined' ? window.playCard : null);
      const card = (getSeatHand(sId) || []).find(c => c.id === msg.cardId);
      if (card && typeof playCard === 'function') {
        playCard(sId, card);
      }
      netPush();
      return;
    }
    if (msg.op === 'discard' || msg.op === 'discardP2') {
      const handleSeatDiscardIntent = context.handleSeatDiscardIntent || (typeof window !== 'undefined' ? window.handleSeatDiscardIntent : NetworkManager.handleSeatDiscardIntent);
      if (typeof handleSeatDiscardIntent === 'function') {
        handleSeatDiscardIntent(sId, msg.ids || []);
      }
      return;
    }
    if (msg.op === 'draw') {
      const confirmDraw = context.confirmDraw || (typeof window !== 'undefined' ? window.confirmDraw : null);
      if (typeof confirmDraw === 'function') {
        state.selected = msg.ids || [];
        confirmDraw(context);
      }
      return;
    }
    if (msg.op === 'playAgain') {
      const playAgain = context.playAgain || (typeof window !== 'undefined' ? window.playAgain : null);
      if (typeof playAgain === 'function') playAgain();
      return;
    }
    if (msg.op === 'wild') {
      const getSeatHand = context.getSeatHand || (typeof window !== 'undefined' ? window.getSeatHand : () => []);
      const confirmWildAs = context.confirmWildAs || (typeof window !== 'undefined' ? window.confirmWildAs : null);
      const hands = [].concat(getSeatHand(sId) || [], (state && state.starters && state.starters.length) ? state.starters : (state && state.starter ? [state.starter] : []));
      const c = hands.find(x => x && x.id === msg.cardId);
      if (c && state) {
        state.pendingWild = c;
        if (typeof confirmWildAs === 'function') {
          confirmWildAs(msg.r, msg.s, sId);
        }
      }
      netPush();
    }
  }

  /**
   * Welcomes and allocates seat to a connecting client.
   * @param {Object} conn
   * @param {Object} msg
   * @param {Object} [context]
   */
  static handleClientHello(conn, msg, context = {}) {
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
    const net = context.net || (typeof window !== 'undefined' ? window.net : null);
    if (!settings || !net) return;

    const count = settings.playerCount || 2;
    const takenSeats = new Set();
    if (net.clients) {
      for (const client of net.clients.values()) {
        if (client.seatId && client.conn && client.conn.peer !== conn.peer) {
          takenSeats.add(client.seatId);
        }
      }
    }

    let assignedSeatId = null;
    for (let i = 2; i <= count; i++) {
      const sId = 'p' + i;
      if (!takenSeats.has(sId)) {
        assignedSeatId = sId;
        break;
      }
    }

    if (!assignedSeatId) {
      try {
        conn.send({ type: 'error', message: 'Room is full' });
        conn.close();
      } catch (e) {}
      return;
    }

    conn._seatId = assignedSeatId;
    const fallbackClientName = `Player ${assignedSeatId.slice(1)}`;
    const clientName = NetworkManager.sanitizePlayerName(msg && msg.name, fallbackClientName);
    if (!net.clients) net.clients = new Map();
    net.clients.set(conn.peer, {
      conn,
      seatId: assignedSeatId,
      name: clientName
    });

    net.conn = conn;
    net.ready = true;

    const doc = context.document || (typeof document !== 'undefined' ? document : null);
    const hostInput = doc ? doc.getElementById('optPlayerName') : null;
    const s = getStorage(context);
    const hostName = NetworkManager.sanitizePlayerName(
      (hostInput && hostInput.value.trim()) || (s ? (s.getItem('cc_player_name') || '').trim() : ''),
      'Host'
    );

    const avMgr = context.AvatarManager || (typeof window !== 'undefined' && window.AvatarManager) || AvatarManager || null;
    const hostAvId = avMgr && typeof avMgr.getSelectedAvatarId === 'function' ? avMgr.getSelectedAvatarId(s) : 'jack';
    const hostCustomData = (hostAvId === 'custom' && avMgr && typeof avMgr.getCustomAvatarData === 'function') ? avMgr.getCustomAvatarData(s) : null;
    const clientAvId = (msg && msg.avatarId) || 'jack';
    const clientCustomData = (clientAvId === 'custom' && msg && msg.customAvatarData) ? msg.customAvatarData : null;

    if (clientAvId === 'custom' && clientCustomData && avMgr && typeof avMgr.registerRemoteCustomAvatar === 'function') {
      avMgr.registerRemoteCustomAvatar(assignedSeatId, clientCustomData, clientName);
    }

    if (settings) {
      if (!settings.seats || !settings.seats.length) {
        settings.seats = [
          { id: 'p1', name: hostName, type: 'human', personality: 'shark', team: 1, color: '#d4a017', online: true, avatar: hostAvId, customAvatarData: hostCustomData },
          { id: 'p2', name: clientName, type: 'human', personality: 'shark', team: 2, color: '#ef4444', online: true, avatar: clientAvId, customAvatarData: clientCustomData }
        ];
      } else {
        if (settings.seats[0]) {
          settings.seats[0].name = hostName;
          settings.seats[0].type = 'human';
          settings.seats[0].online = true;
          settings.seats[0].avatar = hostAvId;
          if (hostCustomData) settings.seats[0].customAvatarData = hostCustomData;
        }
        const seatObj = settings.seats.find(s => s && s.id === assignedSeatId);
        if (seatObj) {
          seatObj.name = clientName;
          seatObj.type = 'human';
          seatObj.online = true;
          seatObj.avatar = clientAvId;
          if (clientCustomData) seatObj.customAvatarData = clientCustomData;
        }
      }
    }

    // Update 2D peg pattern & 3D peg textures
    const BoardRenderer = context.BoardRenderer || (typeof window !== 'undefined' ? window.BoardRenderer : null);
    if (BoardRenderer && typeof BoardRenderer.updatePegAvatarPattern === 'function') {
      const targetPattern = (clientAvId === 'custom') ? ('custom_' + assignedSeatId) : clientAvId;
      BoardRenderer.updatePegAvatarPattern(assignedSeatId, targetPattern);
    }
    const bRenderer3D = context.boardRenderer3D || (typeof window !== 'undefined' ? (window.boardRenderer3D || window._currentRenderer3D) : null);
    if (bRenderer3D && typeof bRenderer3D.updatePegAvatars === 'function') {
      bRenderer3D.updatePegAvatars();
    }
    if (typeof window !== 'undefined') {
      window._currentRoster = null;
    }

    const onlineSeats = ['p1'];
    if (net.clients) {
      for (const c of net.clients.values()) {
        if (c && c.seatId && c.conn && c.conn.open) {
          onlineSeats.push(c.seatId);
        }
      }
    }

    const getLobbyRosterData = context.getLobbyRosterData || (typeof window !== 'undefined' ? window.getLobbyRosterData : () => []);
    try {
      conn.send({
        type: 'welcome',
        seatId: assignedSeatId,
        room: net.room,
        playerCount: count,
        roster: typeof getLobbyRosterData === 'function' ? getLobbyRosterData() : [],
        seats: settings.seats,
        onlineSeats: Array.from(new Set(onlineSeats))
      });
    } catch (e) {
      console.warn('Welcome send error:', e);
    }

    const broadcastLobbyUpdate = context.broadcastLobbyUpdate || (typeof window !== 'undefined' ? window.broadcastLobbyUpdate : NetworkManager.broadcastLobbyUpdate);
    if (typeof broadcastLobbyUpdate === 'function') broadcastLobbyUpdate();

    const updateNetRoster = context.updateNetRoster || (typeof window !== 'undefined' ? window.updateNetRoster : NetworkManager.updateNetRoster);
    if (typeof updateNetRoster === 'function') updateNetRoster();

    const setNetStatus = context.setNetStatus || (typeof window !== 'undefined' ? window.setNetStatus : () => {});
    const humanCount = net.clients.size + 1;
    setNetStatus(`${humanCount} player(s) in room. Ready to start!`, 'on');

    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    const netPush = context.netPush || (typeof window !== 'undefined' ? window.netPush : NetworkManager.netPush);
    if (state && !state.over && typeof netPush === 'function') {
      netPush();
    }

    // If host is broadcasting camera, initiate call to the newly joined peer
    try {
      const camMgr = (context.CameraManager && context.CameraManager.getInstance ? context.CameraManager.getInstance() : CameraManager.getInstance());
      if (camMgr && camMgr.isBroadcasting && conn.peer) {
        camMgr.callPeer(conn.peer, assignedSeatId, context);
      }
    } catch (e) {
      console.warn('[CameraManager] callPeer on hello error:', e);
    }
  }

  /**
   * Handles peer disconnection on the host, converting mid-game seats to CPU.
   * @param {Object} conn
   * @param {Object} [context]
   */
  /**
   * Checks whether the current game has more than 1 human player.
   * @param {Object} [settings]
   * @returns {boolean}
   */
  static isMultiHuman(settings) {
    if (!settings) return false;
    if (Array.isArray(settings.seats)) {
      const humans = settings.seats.filter(s => s && s.type === 'human');
      if (humans.length >= 2) return true;
    }
    return Boolean(settings.opp === 'online' || settings.opp === 'hvh' || settings.isHvH);
  }

  /**
   * Checks whether a seat is currently connected online.
   * @param {string} seatId
   * @param {Object} [context]
   * @returns {boolean}
   */
  static isSeatOnline(seatId, context = {}) {
    const net = context.net || (typeof window !== 'undefined' ? window.net : null);
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
    const sId = String(seatId || 'p1').toLowerCase();

    // Check if the seat is marked as CPU or explicitly marked offline
    if (settings && settings.seats) {
      const s = settings.seats.find(x => x && x.id === sId);
      if (s && s.type !== 'human') return false;
      if (s && s.online === false) return false;
    }

    const isHvH = Boolean(settings && (settings.isHvH || settings.opp === 'hvh'));
    if (isHvH) return true;

    if (!settings || settings.opp !== 'online') {
      return false;
    }

    // Determine host vs guest role reliably
    const isHost = Boolean(
      (settings && settings.netRole === 'host') ||
      (net && (net.isHost === true || net.role === 'host'))
    );
    const isGuest = Boolean(
      (settings && settings.netRole === 'guest') ||
      (net && (net.isHost === false || net.role === 'guest')) ||
      (!isHost && net && (net.conn || net.mySeatId))
    );

    // Host perspective: host player (p1) is always online locally at host machine
    if (isHost) {
      if (sId === 'p1') return true;
      if (net && net.clients) {
        for (const [, c] of net.clients.entries()) {
          if (c && c.seatId === sId) {
            return Boolean(c.conn && c.conn.open);
          }
        }
      }
      return false;
    }

    // Guest perspective:
    if (isGuest) {
      const isConnectedToHost = Boolean(net && (net.conn ? net.conn.open : net.ready) && !net._handledHostDisconnect);
      if (!isConnectedToHost) {
        return false;
      }
      // Host seat (p1) is online if guest is connected to host and host is not marked offline
      if (sId === 'p1') {
        if (settings && settings.seats) {
          const s = settings.seats.find(x => x && x.id === 'p1');
          if (s && s.online === false) return false;
        }
        return true;
      }
      const mySeat = (net && net.mySeatId) ? net.mySeatId : 'p2';
      // Local guest's own seat is online if connected
      if (sId === mySeat) {
        return true;
      }
      // Check synced online seats list broadcast by host
      if (net && Array.isArray(net.onlineSeats)) {
        return net.onlineSeats.includes(sId);
      }
      if (settings && settings.seats) {
        const s = settings.seats.find(x => x && x.id === sId);
        if (s && s.online !== undefined) return Boolean(s.online);
      }
      return false;
    }

    // Fallback if role is not explicitly set
    if (settings && settings.seats) {
      const s = settings.seats.find(x => x && x.id === sId);
      if (s && s.online !== undefined) return Boolean(s.online);
    }
    if (sId === 'p1') return true;
    if (net && net.conn) return Boolean(net.conn.open);
    return false;
  }

  /**
   * Handles peer disconnection on the host, notifying player and offering AI replacement.
   * @param {Object} conn
   * @param {Object} [context]
   */
  static handleClientDisconnect(conn, context = {}) {
    if (!conn) return;
    if (conn._handledDisconnect) return;
    conn._handledDisconnect = true;

    const net = context.net || (typeof window !== 'undefined' ? window.net : null);
    if (!net) return;

    let client = net.clients ? net.clients.get(conn.peer) : null;
    if (!client && net.clients) {
      for (const c of net.clients.values()) {
        if (c && (c.conn === conn || (c.seatId && c.seatId === conn._seatId))) {
          client = c;
          break;
        }
      }
    }

    const sId = client ? client.seatId : conn._seatId;
    const sName = client ? client.name : (sId ? `Player ${sId.slice(1)}` : 'A player');

    if (sId) {
      try {
        const camMgr = (context.CameraManager && context.CameraManager.getInstance ? context.CameraManager.getInstance() : CameraManager.getInstance());
        if (camMgr) camMgr.removeRemoteTile(sId, context);
      } catch (e) {
        console.warn('[CameraManager] removeRemoteTile on disconnect error:', e);
      }
    }

    if (net.clients) {
      if (conn.peer) net.clients.delete(conn.peer);
      for (const [pId, c] of net.clients.entries()) {
        if (c && (c.conn === conn || c.seatId === sId)) {
          net.clients.delete(pId);
        }
      }
      if (net.conn === conn) {
        net.conn = net.clients.values().next().value ? net.clients.values().next().value.conn : null;
      }
      if (net.clients.size === 0) {
        net.ready = false;
      }
    }

    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
    if (sId && settings && settings.seats) {
      const s = settings.seats.find(x => x && x.id === sId);
      if (s) {
        s.online = false;
      }
    }

    // Recompute remaining online seats on host
    const remainingOnline = ['p1'];
    if (net && net.clients) {
      for (const c of net.clients.values()) {
        if (c && c.seatId && c.seatId !== sId && c.conn && c.conn.open) {
          remainingOnline.push(c.seatId);
        }
      }
    }
    if (net) {
      net.onlineSeats = Array.from(new Set(remainingOnline));
    }

    // Notify any remaining connected clients that this player went offline
    if (net && net.clients) {
      for (const c of net.clients.values()) {
        if (c && c.conn && c.conn.open) {
          try {
            c.conn.send({
              type: 'player_offline',
              seatId: sId,
              name: sName,
              onlineSeats: net.onlineSeats
            });
          } catch (e) {}
        }
      }
    }

    const broadcastLobbyUpdate = context.broadcastLobbyUpdate || (typeof window !== 'undefined' ? window.broadcastLobbyUpdate : NetworkManager.broadcastLobbyUpdate);
    if (typeof broadcastLobbyUpdate === 'function') broadcastLobbyUpdate();

    const updateNetRoster = context.updateNetRoster || (typeof window !== 'undefined' ? window.updateNetRoster : NetworkManager.updateNetRoster);
    if (typeof updateNetRoster === 'function') updateNetRoster();

    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    const setNetStatus = context.setNetStatus || (typeof window !== 'undefined' ? window.setNetStatus : () => {});
    const log = context.log || (typeof window !== 'undefined' ? window.log : () => {});
    const netPush = context.netPush || (typeof window !== 'undefined' ? window.netPush : NetworkManager.netPush);

    setNetStatus(`${sName} went offline.`, 'wait');
    log(`${sName} went offline.`);

    const renderScores = context.renderScores || (typeof window !== 'undefined' ? window.renderScores : null);
    if (typeof renderScores === 'function') renderScores(false);
    const render = context.render || (typeof window !== 'undefined' ? window.render : null);
    if (typeof render === 'function') render();

    if (typeof netPush === 'function') {
      netPush({}, context);
    }

    if (state && !state.over) {
      const onPlayerOffline = context.onPlayerOffline || (typeof window !== 'undefined' ? window.handlePlayerOfflinePrompt : null);
      if (typeof onPlayerOffline === 'function') {
        onPlayerOffline(sId, sName);
      } else {
        NetworkManager.replaceSeatWithCpu(sId, 'shark', 'The Shark', context);
      }
    }
  }

  /**
   * Handles host disconnection on the guest client.
   * @param {Object} [context]
   */
  static handleHostDisconnect(context = {}) {
    const net = context.net || (typeof window !== 'undefined' ? window.net : null);
    if (!net) return;
    if (net._handledHostDisconnect) return;
    net._handledHostDisconnect = true;

    net.ready = false;
    if (net.conn) {
      try { net.conn.open = false; } catch (e) {}
    }
    net.onlineSeats = [];

    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
    if (settings && settings.seats) {
      settings.seats.forEach(s => {
        if (s && s.id === 'p1') {
          s.online = false;
        }
      });
    }

    const hideWait = context.hideWait || (typeof window !== 'undefined' ? window.hideWait : () => {});
    hideWait();

    const setNetStatus = context.setNetStatus || (typeof window !== 'undefined' ? window.setNetStatus : () => {});
    setNetStatus('Host disconnected from room.', 'wait');

    const log = context.log || (typeof window !== 'undefined' ? window.log : () => {});
    log('Host disconnected from room.');

    const renderScores = context.renderScores || (typeof window !== 'undefined' ? window.renderScores : null);
    if (typeof renderScores === 'function') renderScores(false);
    const render = context.render || (typeof window !== 'undefined' ? window.render : null);
    if (typeof render === 'function') render();

    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    const onPlayerOffline = context.onPlayerOffline || (typeof window !== 'undefined' ? window.handlePlayerOfflinePrompt : null);
    if (state && !state.over && typeof onPlayerOffline === 'function') {
      onPlayerOffline('p1', 'Host');
    }
  }

  /**
   * Replaces a disconnected human seat with a designated computer player personality.
   * @param {string} seatId
   * @param {string} [aiProfile='shark']
   * @param {string} [personalityName='The Shark']
   * @param {Object} [context]
   */
  static replaceSeatWithCpu(seatId, aiProfile = 'shark', personalityName = 'The Shark', context = {}) {
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    const setNetStatus = context.setNetStatus || (typeof window !== 'undefined' ? window.setNetStatus : () => {});
    const log = context.log || (typeof window !== 'undefined' ? window.log : () => {});
    const netPush = context.netPush || (typeof window !== 'undefined' ? window.netPush : NetworkManager.netPush);

    let updatedSeatName = `${personalityName} (${(seatId || '').toUpperCase()})`;
    if (seatId && settings && settings.seats) {
      const s = settings.seats.find(x => x.id === seatId);
      if (s) {
        s.type = 'cpu';
        s.personality = aiProfile;
        s.name = `${personalityName} (${seatId.toUpperCase()})`;
        s.online = false;
        updatedSeatName = s.name;
      }
    }

    if (typeof window !== 'undefined') {
      window._currentRoster = null;
    }

    setNetStatus(`${personalityName} took over ${seatId.toUpperCase()}.`, 'on');
    log(`Computer player ${personalityName} (${aiProfile.toUpperCase()}) took over seat ${seatId.toUpperCase()}.`);

    if (typeof netPush === 'function') {
      netPush({
        log: `${personalityName} took over ${seatId.toUpperCase()}.`,
        seats: settings ? settings.seats : undefined
      });
    }

    const renderScores = context.renderScores || (typeof window !== 'undefined' ? window.renderScores : null);
    if (typeof renderScores === 'function') renderScores(false);
    const render = context.render || (typeof window !== 'undefined' ? window.render : null);
    if (typeof render === 'function') render();

    if (state && !state.over) {
      if (state.turn === seatId && state.phase === 'play') {
        const scheduleOppPlay = context.scheduleOppPlay || (typeof window !== 'undefined' ? window.scheduleOppPlay : null);
        if (typeof scheduleOppPlay === 'function') {
          scheduleOppPlay(seatId);
        }
      } else if (state.phase === 'discard') {
        const kept = state.keptCards ? state.keptCards[seatId] : null;
        if (!kept || !kept.length) {
          const chooseDiscard = context.chooseDiscard || (typeof window !== 'undefined' ? window.chooseDiscard : null);
          const handleSeatDiscardIntent = context.handleSeatDiscardIntent || (typeof window !== 'undefined' ? window.handleSeatDiscardIntent : null);
          const sHand = state.hands ? state.hands[seatId] : [];
          if (typeof chooseDiscard === 'function' && typeof handleSeatDiscardIntent === 'function' && sHand && sHand.length > 4) {
            const isDealer = (state.dealer === seatId);
            const discardN = (parseInt(settings?.playerCount, 10) === 2) ? 2 : 1;
            const toDeck = Boolean(settings?.optNoPassingLanes);
            const cpuThrow = chooseDiscard(sHand, isDealer, settings?.goal === 'low', discardN, toDeck);
            if (cpuThrow && cpuThrow.length) {
              handleSeatDiscardIntent(seatId, cpuThrow.map(c => c.id || c));
            }
          }
        }
      } else if (state.phase === 'cut' || state.phase === 'cut-pick') {
        const pickCut = context.pickCut || (typeof window !== 'undefined' ? window.pickCut : null);
        if (typeof pickCut === 'function') {
          pickCut(seatId, 0);
        }
      }
    }
  }

  /**
   * Binds data, close, and error listeners on incoming client connection.
   * @param {Object} conn
   * @param {Object} [context]
   */
  static bindHostClientConn(conn, context = {}) {
    conn.on('open', () => {});
    conn.on('data', (msg) => {
      if (!msg || typeof msg !== 'object') return;
      const net = context.net || (typeof window !== 'undefined' ? window.net : null);
      if (net && net.clients) {
        const client = net.clients.get(conn.peer);
        if (client) client.lastSeen = Date.now();
      }

      if (msg.type === 'client_leave' || msg.type === 'leave' || msg.type === 'disconnect') {
        const handleClientDisconnect = context.handleClientDisconnect || (typeof window !== 'undefined' ? window.handleClientDisconnect : NetworkManager.handleClientDisconnect);
        if (typeof handleClientDisconnect === 'function') handleClientDisconnect(conn, context);
        return;
      } else if (msg.type === 'pong') {
        return;
      } else if (msg.type === 'ping') {
        try { conn.send({ type: 'pong', seatId: 'p1' }); } catch (e) {}
        return;
      } else if (msg.type === 'hello') {
        const handleClientHello = context.handleClientHello || (typeof window !== 'undefined' ? window.handleClientHello : NetworkManager.handleClientHello);
        if (typeof handleClientHello === 'function') handleClientHello(conn, msg);
      } else if (msg.type === 'intent') {
        const hostApplyIntent = context.hostApplyIntent || (typeof window !== 'undefined' ? window.hostApplyIntent : NetworkManager.hostApplyIntent);
        if (typeof hostApplyIntent === 'function') hostApplyIntent(msg, conn);
      } else if (msg.type === 'cam_state') {
        try {
          const camMgr = (context.CameraManager && context.CameraManager.getInstance ? context.CameraManager.getInstance() : CameraManager.getInstance());
          if (camMgr) camMgr.handleCamStateMessage(msg, context);
          // Relay camera state to all other connected clients
          if (net && net.clients) {
            for (const [pId, c] of net.clients.entries()) {
              if (c && c.conn && pId !== conn.peer) {
                try { c.conn.send(msg); } catch (e) {}
              }
            }
          }
        } catch (e) {
          console.warn('[CameraManager] Host cam_state relay error:', e);
        }
      } else if (msg.type === 'chat') {
        try {
          const chatMgr = (context.ChatManager && context.ChatManager.getInstance ? context.ChatManager.getInstance() : ChatManager.getInstance());
          if (chatMgr) chatMgr.handleIncomingMessage(msg, context);
          // Relay chat message to all other connected clients
          if (net && net.clients) {
            for (const [pId, c] of net.clients.entries()) {
              if (c && c.conn && pId !== conn.peer) {
                try { c.conn.send(msg); } catch (e) {}
              }
            }
          }
        } catch (e) {
          console.warn('[ChatManager] Host chat relay error:', e);
        }
      } else if (msg.type === 'reaction') {
        try {
          const sfx = context.sfx || (typeof window !== 'undefined' ? window.sfx : null);
          ReactionManager.handleRemoteReaction(msg, { sfx });
          if (net && net.clients) {
            for (const [pId, c] of net.clients.entries()) {
              if (c && c.conn && pId !== conn.peer) {
                try { c.conn.send(msg); } catch (e) {}
              }
            }
          }
        } catch (e) {
          console.warn('[ReactionManager] Host reaction relay error:', e);
        }
      } else if (msg.type === 'tournament_update') {
        try {
          if (net && net.clients) {
            for (const [pId, c] of net.clients.entries()) {
              if (c && c.conn && pId !== conn.peer) {
                try { c.conn.send(msg); } catch (e) {}
              }
            }
          }
        } catch (e) {
          console.warn('[Tournament] Host tournament relay error:', e);
        }
      } else if (msg.type === 'avatar_update') {
        try {
          NetworkManager.handleAvatarUpdate(msg, context);
          if (net && net.clients) {
            for (const [pId, c] of net.clients.entries()) {
              if (c && c.conn && pId !== conn.peer) {
                try { c.conn.send(msg); } catch (e) {}
              }
            }
          }
        } catch (e) {
          console.warn('[AvatarManager] Host avatar relay error:', e);
        }
      }
    });

    const triggerDisconnect = () => {
      const handleClientDisconnect = context.handleClientDisconnect || (typeof window !== 'undefined' ? window.handleClientDisconnect : NetworkManager.handleClientDisconnect);
      if (typeof handleClientDisconnect === 'function') handleClientDisconnect(conn, context);
    };

    conn.on('close', triggerDisconnect);
    conn.on('error', (err) => {
      console.warn('Client conn error:', err);
      triggerDisconnect();
    });

    if (conn.peerConnection) {
      try {
        conn.peerConnection.addEventListener('connectionstatechange', () => {
          const st = conn.peerConnection.connectionState;
          if (st === 'disconnected' || st === 'failed' || st === 'closed') {
            triggerDisconnect();
          }
        });
        conn.peerConnection.addEventListener('iceconnectionstatechange', () => {
          const st = conn.peerConnection.iceConnectionState;
          if (st === 'disconnected' || st === 'failed' || st === 'closed') {
            triggerDisconnect();
          }
        });
      } catch (e) {}
    }
  }

  /**
   * Displays the host invite link box in lobby.
   * @param {string} code
   * @param {Object} [context]
   */
  static showHostInviteLink(code, context = {}) {
    const getJoinUrl = context.getJoinUrl || (typeof window !== 'undefined' && typeof window.getJoinUrl === 'function' ? window.getJoinUrl : NetworkManager.getJoinUrl);
    const joinUrl = getJoinUrl(code);
    const doc = context.document || (typeof document !== 'undefined' ? document : null);
    if (!doc) return;
    const btnCopy = doc.getElementById('btnCopyRoomLink');
    const bar = doc.getElementById('netInviteBox');
    const inp = doc.getElementById('netInviteUrl');
    const shareBox = doc.getElementById('netShareBox');
    if (btnCopy) btnCopy.style.display = 'inline-flex';
    if (bar && inp) {
      inp.value = joinUrl;
      bar.style.display = 'flex';
    }
    if (shareBox) {
      shareBox.style.display = 'flex';
    }
  }

  /**
   * Hides the host invite link box and share box.
   * @param {Object} [context]
   */
  static hideHostInviteLink(context = {}) {
    const doc = context.document || (typeof document !== 'undefined' ? document : null);
    if (!doc) return;
    const btnCopy = doc.getElementById('btnCopyRoomLink');
    const bar = doc.getElementById('netInviteBox');
    const shareBox = doc.getElementById('netShareBox');
    if (btnCopy) btnCopy.style.display = 'none';
    if (bar) bar.style.display = 'none';
    if (shareBox) shareBox.style.display = 'none';
  }

  /**
   * Builds structured room share data.
   * @param {Object} [context]
   * @returns {{code: string, joinUrl: string, title: string, text: string}}
   */
  static getRoomShareData(context = {}) {
    const doc = context.document || (typeof document !== 'undefined' ? document : null);
    const net = context.net || (typeof window !== 'undefined' ? window.net : null);
    const optRoom = doc ? doc.getElementById('optRoom') : null;
    const code = (net && net.room) || (optRoom && optRoom.value) || '';
    const cleanCode = code.trim().toUpperCase();
    const getJoinUrl = context.getJoinUrl || (typeof window !== 'undefined' && typeof window.getJoinUrl === 'function' ? window.getJoinUrl : NetworkManager.getJoinUrl);
    const joinUrl = cleanCode ? getJoinUrl(cleanCode) : (typeof window !== 'undefined' && window.location ? window.location.href : '');
    const title = 'Crazy Cribbage Online';
    const text = cleanCode
      ? `Join my Crazy Cribbage online room! Room code: ${cleanCode}`
      : 'Play Crazy Cribbage online!';
    return { code: cleanCode, joinUrl, title, text };
  }

  /**
   * Triggers native Web Share API or falls back to the full share modal.
   * @param {Object} [context]
   */
  static shareRoomInvite(context = {}) {
    const { code, joinUrl, title, text } = NetworkManager.getRoomShareData(context);
    const setNetStatus = context.setNetStatus || (typeof window !== 'undefined' ? window.setNetStatus : () => {});
    if (!code) {
      setNetStatus('Please host or enter a room code first.', '');
      return;
    }
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      navigator.share({
        title,
        text,
        url: joinUrl
      }).catch((err) => {
        if (err && err.name !== 'AbortError') {
          if (typeof window !== 'undefined' && typeof window.showShareModal === 'function') {
            window.showShareModal();
          }
        }
      });
    } else if (typeof window !== 'undefined' && typeof window.showShareModal === 'function') {
      window.showShareModal();
    } else {
      NetworkManager.copyRoomInviteLink(null, context);
    }
  }

  /**
   * Quick share via WhatsApp.
   * @param {Object} [context]
   */
  static shareRoomWhatsApp(context = {}) {
    const { code, joinUrl, text } = NetworkManager.getRoomShareData(context);
    if (!code) return;
    const shareMsg = `${text}\n${joinUrl}`;
    const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareMsg)}`;
    if (typeof window !== 'undefined') window.open(url, '_blank', 'noopener,noreferrer');
  }

  /**
   * Quick share via Email.
   * @param {Object} [context]
   */
  static shareRoomEmail(context = {}) {
    const { code, joinUrl, text } = NetworkManager.getRoomShareData(context);
    if (!code) return;
    const subject = `Join my Crazy Cribbage Game (Room: ${code})`;
    const body = `Hey,\n\nJoin my Crazy Cribbage online game!\n\nRoom Code: ${code}\nInvite Link: ${joinUrl}\n\nClick the link above to join directly from your browser!`;
    const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    if (typeof window !== 'undefined') window.location.href = mailto;
  }

  /**
   * Copies host invite link to user clipboard with visual feedback.
   * @param {HTMLElement} [btnEl]
   * @param {Object} [context]
   */
  static copyRoomInviteLink(btnEl, context = {}) {
    const doc = context.document || (typeof document !== 'undefined' ? document : null);
    const net = context.net || (typeof window !== 'undefined' ? window.net : null);
    const optRoom = doc ? doc.getElementById('optRoom') : null;
    const code = (net && net.room) || (optRoom && optRoom.value) || '';
    const cleanCode = code.trim().toUpperCase();

    const setNetStatus = context.setNetStatus || (typeof window !== 'undefined' ? window.setNetStatus : () => {});
    if (!cleanCode) {
      setNetStatus('Please host or enter a room code first.', '');
      return;
    }

    const getJoinUrl = context.getJoinUrl || (typeof window !== 'undefined' && typeof window.getJoinUrl === 'function' ? window.getJoinUrl : NetworkManager.getJoinUrl);
    const joinUrl = getJoinUrl(cleanCode);
    const targetBtn = btnEl || (doc ? doc.getElementById('btnCopyRoomLink') : null);
    const txtSpan = doc ? doc.getElementById('btnCopyRoomLinkText') : null;
    const barBtn = doc ? doc.getElementById('btnCopyRoomLinkBar') : null;

    const onCopied = () => {
      if (txtSpan) txtSpan.textContent = 'Copied!';
      else if (targetBtn) targetBtn.textContent = 'Copied!';
      if (barBtn) barBtn.textContent = 'Copied!';
      if (targetBtn) targetBtn.style.filter = 'brightness(1.25)';
      setNetStatus('Invite link copied! Share it with friends to join…', 'wait');
      setTimeout(() => {
        if (txtSpan) txtSpan.textContent = 'Copy Link';
        else if (targetBtn) targetBtn.textContent = 'Copy Link';
        if (barBtn) barBtn.textContent = 'Copy';
        if (targetBtn) targetBtn.style.filter = '';
      }, 2500);
    };

    const nav = typeof navigator !== 'undefined' ? navigator : null;
    if (nav && nav.clipboard && nav.clipboard.writeText) {
      nav.clipboard.writeText(joinUrl).then(onCopied).catch(() => {
        NetworkManager.fallbackCopyText(joinUrl, onCopied, doc);
      });
    } else {
      NetworkManager.fallbackCopyText(joinUrl, onCopied, doc);
    }
  }

  /**
   * Fallback copy mechanism using temporary textarea element.
   * @param {string} text
   * @param {Function} [cb]
   * @param {Document} [doc]
   */
  static fallbackCopyText(text, cb, doc = null) {
    const documentObj = doc || (typeof document !== 'undefined' ? document : null);
    if (!documentObj) {
      if (cb) cb();
      return;
    }
    const inp = documentObj.getElementById('netInviteUrl');
    if (inp) {
      inp.value = text;
      inp.select();
      try {
        documentObj.execCommand('copy');
        if (cb) cb();
        return;
      } catch (e) {}
    }
    const temp = documentObj.createElement('textarea');
    temp.value = text;
    temp.style.position = 'fixed';
    temp.style.opacity = '0';
    documentObj.body.appendChild(temp);
    temp.select();
    try {
      documentObj.execCommand('copy');
      if (cb) cb();
    } catch (e) {
      if (typeof prompt === 'function') prompt('Copy this invite link:', text);
    }
    if (temp.parentNode) temp.parentNode.removeChild(temp);
  }

  /**
   * Prompts the player to enter their name before joining a multiplayer room.
   * @param {string} code
   * @param {Function} onConfirm
   * @param {Object} [context]
   */
  static promptPlayerName(code, onConfirm, context = {}) {
    const doc = context.document || (typeof document !== 'undefined' ? document : null);
    const showModal = context.showModal || (typeof window !== 'undefined' ? window.showModal : null);
    const hideModal = context.hideModal || (typeof window !== 'undefined' ? window.hideModal : null);
    let initialName = '';
    const s = getStorage(context);
    try {
      initialName = (doc && doc.getElementById('optPlayerName') && doc.getElementById('optPlayerName').value.trim())
        || (s ? (s.getItem('cc_player_name') || '').trim() : '');
    } catch (e) {}

    const html = `
      <div style="text-align:center;padding:16px 12px;max-width:380px;margin:0 auto;">
        <div style="font-size:2.2rem;margin-bottom:8px;">👋</div>
        <h2 style="margin:0 0 8px;color:#f0c64a;font-size:1.35rem;letter-spacing:0.03em;">Join Room ${code || 'Game'}</h2>
        <p style="margin:0 0 16px;color:#f4ead4;font-size:0.95rem;line-height:1.4;">Please enter your name so scoring, bonus options, and match results can be displayed for you and your opponent:</p>
        <form id="joinNameForm" style="display:flex;flex-direction:column;align-items:center;gap:12px;width:100%;">
          <input id="joinPlayerNameInput" maxlength="16" placeholder="Your Name" value="${initialName.replace(/"/g, '&quot;')}" pattern="[a-zA-Z0-9]+" autofocus
            style="width:90%;max-width:260px;padding:10px 14px;border-radius:8px;border:2px solid #f0c64a;font-size:1.05rem;font-weight:700;background:#f4ead4 !important;background-color:#f4ead4 !important;color:#111 !important;-webkit-text-fill-color:#111 !important;caret-color:#111 !important;color-scheme:light !important;text-align:center;outline:none;" />
          <p id="joinNameError" style="display:none;margin:-4px 0 0;font-size:0.85rem;color:#ef4444;font-weight:600;">Please enter a name to join the game.</p>
          <div style="display:flex;gap:10px;margin-top:6px;width:100%;justify-content:center;">
            <button type="submit" class="btn-gold" id="btnConfirmJoinName" style="padding:8px 24px;font-size:1rem;font-weight:700;">Join Room</button>
            <button type="button" class="btn-ghost" id="btnCancelJoinName" style="padding:8px 16px;font-size:0.95rem;">Cancel</button>
          </div>
        </form>
      </div>
    `;

    if (typeof showModal === 'function') {
      showModal(html, false);
      if (doc) {
        const inp = doc.getElementById('joinPlayerNameInput');
        const err = doc.getElementById('joinNameError');
        const form = doc.getElementById('joinNameForm');
        const btnOk = doc.getElementById('btnConfirmJoinName');
        const btnCancel = doc.getElementById('btnCancelJoinName');

        const doJoin = () => {
          const val = inp ? sanitizeUserInput(inp.value).replace(/[^a-zA-Z0-9]/g, '').trim() : '';
          if (!val) {
            if (err) err.style.display = 'block';
            if (inp) {
              inp.style.borderColor = '#ef4444';
              inp.focus();
            }
            return;
          }
          try { if (s) s.setItem('cc_player_name', val); } catch (e) {}
          const opt = doc.getElementById('optPlayerName');
          if (opt) opt.value = val;
          if (typeof hideModal === 'function') hideModal();
          if (typeof onConfirm === 'function') onConfirm(val);
        };

        if (form && typeof form.addEventListener === 'function') {
          form.addEventListener('submit', (e) => { e.preventDefault(); doJoin(); });
        } else if (form) {
          form.onsubmit = (e) => { e.preventDefault(); doJoin(); };
        }
        if (inp) {
          setTimeout(() => { try { inp.focus(); inp.select(); } catch (e) {} }, 50);
          inp.addEventListener('input', () => {
            inp.value = sanitizeUserInput(inp.value).replace(/[^a-zA-Z0-9]/g, '');
            if (err) err.style.display = 'none';
            inp.style.borderColor = '#f0c64a';
          });
        }
        if (btnOk) btnOk.onclick = (e) => { e.preventDefault(); doJoin(); };
        if (btnCancel) btnCancel.onclick = (e) => {
          e.preventDefault();
          if (typeof hideModal === 'function') hideModal();
          const setNetStatus = context.setNetStatus || (typeof window !== 'undefined' ? window.setNetStatus : () => {});
          setNetStatus('Join cancelled. Enter your name and click Join to enter room.', '');
        };
      }
    } else {
      const val = (typeof window !== 'undefined' && typeof window.prompt === 'function')
        ? window.prompt(`Enter your name to join Room ${code}:`, initialName)
        : initialName;
      if (val) {
        const clean = sanitizeUserInput(val).replace(/[^a-zA-Z0-9]/g, '').trim();
        if (clean) {
          try { if (s) s.setItem('cc_player_name', clean); } catch (e) {}
          const opt = doc ? doc.getElementById('optPlayerName') : null;
          if (opt) opt.value = clean;
          if (typeof onConfirm === 'function') onConfirm(clean);
        }
      }
    }
  }

  /**
   * Hosts a new multiplayer room via PeerJS.
   * @param {Object} [context]
   */
  static hostRoom(context = {}) {
    const PeerClass = context.PeerClass || (typeof Peer !== 'undefined' ? Peer : (typeof window !== 'undefined' ? window.Peer : null));
    const setNetStatus = context.setNetStatus || (typeof window !== 'undefined' ? window.setNetStatus : () => {});
    if (!PeerClass) {
      setNetStatus('PeerJS failed to load. Use http/https, not file://', '');
      return;
    }

    const doc = context.document || (typeof document !== 'undefined' ? document : null);
    const hostInput = doc ? doc.getElementById('optPlayerName') : null;
    const s = getStorage(context);
    const hostName = (hostInput && hostInput.value.trim())
      || (s ? (s.getItem('cc_player_name') || '').trim() : '')
      || 'Host';
    if (hostInput && !hostInput.value) hostInput.value = hostName;
    try { if (hostName && hostName !== 'Host' && s) s.setItem('cc_player_name', hostName); } catch (e) {}

    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
    const net = context.net || (typeof window !== 'undefined' ? window.net : null);
    if (settings) {
      settings.opp = 'online';
      settings.netRole = 'host';
      if (settings.seats && settings.seats[0]) {
        settings.seats[0].name = hostName;
        settings.seats[0].type = 'human';
        settings.seats[0].online = true;
      }
    }
    if (typeof window !== 'undefined') {
      window._currentRoster = null;
    }
    if (net) {
      net.isHost = true;
      net.role = 'host';
      net.mySeatId = 'p1';
      net.clients = new Map();
      net.onlineSeats = ['p1'];
    }

    const optRoom = doc ? doc.getElementById('optRoom') : null;
    const makeRoomCode = context.makeRoomCode || (typeof window !== 'undefined' && typeof window.makeRoomCode === 'function' ? window.makeRoomCode : NetworkManager.generateRoomCode);
    const code = ((optRoom && optRoom.value) || '').trim().toUpperCase() || makeRoomCode();
    if (optRoom) optRoom.value = code;
    if (net) net.room = code;

    const showHostInviteLink = context.showHostInviteLink || (typeof window !== 'undefined' ? window.showHostInviteLink : NetworkManager.showHostInviteLink);
    const updateNetRoster = context.updateNetRoster || (typeof window !== 'undefined' ? window.updateNetRoster : NetworkManager.updateNetRoster);
    const bindHostClientConn = context.bindHostClientConn || (typeof window !== 'undefined' ? window.bindHostClientConn : NetworkManager.bindHostClientConn);
    const hideWait = context.hideWait || (typeof window !== 'undefined' ? window.hideWait : () => {});

    if (typeof showHostInviteLink === 'function') showHostInviteLink(code);
    if (typeof updateNetRoster === 'function') updateNetRoster();

    setNetStatus('Opening room ' + code + '…', 'wait');
    if (net) {
      try { if (net.peer) net.peer.destroy(); } catch (e) {}
      net.peer = new PeerClass('ccb-' + code, DEFAULT_PEER_CONFIG);
      net.peer.on('open', () => {
        net.ready = true;
        setNetStatus('Room ' + code + ' ready. Waiting for players…', 'wait');
        if (typeof showHostInviteLink === 'function') showHostInviteLink(code);
        if (typeof updateNetRoster === 'function') updateNetRoster();
        NetworkManager.startHeartbeat(context);
        NetworkManager.initUnloadHandler(context);
      });
      net.peer.on('connection', (conn) => {
        if (typeof bindHostClientConn === 'function') bindHostClientConn(conn);
      });
      net.peer.on('call', (call) => {
        try {
          const camMgr = (context.CameraManager && context.CameraManager.getInstance ? context.CameraManager.getInstance() : CameraManager.getInstance());
          if (camMgr) camMgr.handleIncomingCall(call, context);
        } catch (e) {
          console.warn('[CameraManager] Host incoming call error:', e);
        }
      });
      net.peer.on('error', (err) => {
        hideWait();
        setNetStatus('Host error: ' + (err ? err.type || err : 'unknown'), '');
      });
    }
  }

  /**
   * Joins an existing host room via PeerJS.
   * @param {string} [codeParam]
   * @param {Object} [context]
   */
  static joinRoom(codeParam = null, context = {}) {
    const PeerClass = context.PeerClass || (typeof Peer !== 'undefined' ? Peer : (typeof window !== 'undefined' ? window.Peer : null));
    const setNetStatus = context.setNetStatus || (typeof window !== 'undefined' ? window.setNetStatus : () => {});
    const hideWait = context.hideWait || (typeof window !== 'undefined' ? window.hideWait : () => {});

    if (!PeerClass) {
      setNetStatus('PeerJS failed to load. Use http/https, not file://', '');
      hideWait();
      return;
    }

    const doc = context.document || (typeof document !== 'undefined' ? document : null);
    const optRoom = doc ? doc.getElementById('optRoom') : null;
    const code = (codeParam || (optRoom && optRoom.value) || '').trim().toUpperCase();
    if (!code) {
      setNetStatus('Enter the host room code first.', '');
      hideWait();
      return;
    }

    const optPlayerName = doc ? doc.getElementById('optPlayerName') : null;
    const s = getStorage(context);
    let myName = (context.playerName || (optPlayerName && optPlayerName.value.trim())
      || (s ? (s.getItem('cc_player_name') || '').trim() : '')).trim();

    if (!myName) {
      hideWait();
      NetworkManager.promptPlayerName(code, (enteredName) => {
        NetworkManager.joinRoom(code, { ...context, playerName: enteredName });
      }, context);
      return;
    }

    if (optPlayerName && !optPlayerName.value) optPlayerName.value = myName;
    try { if (s) s.setItem('cc_player_name', myName); } catch (e) {}

    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
    const net = context.net || (typeof window !== 'undefined' ? window.net : null);
    if (settings) {
      settings.opp = 'online';
      settings.netRole = 'guest';
    }
    if (net) {
      net.isHost = false;
      net.role = 'guest';
      net.room = code;
    }

    setNetStatus('Joining ' + code + '…', 'wait');
    if (net) {
      try { if (net.peer) net.peer.destroy(); } catch (e) {}
      net.peer = new PeerClass(DEFAULT_PEER_CONFIG);
      net.peer.on('call', (call) => {
        try {
          const camMgr = (context.CameraManager && context.CameraManager.getInstance ? context.CameraManager.getInstance() : CameraManager.getInstance());
          if (camMgr) camMgr.handleIncomingCall(call, context);
        } catch (e) {
          console.warn('[CameraManager] Guest incoming call error:', e);
        }
      });
      net.peer.on('open', () => {
        const conn = net.peer.connect('ccb-' + code, { reliable: true });
        net.conn = conn;
        conn.on('open', () => {
          hideWait();
          const avMgr = context.AvatarManager || (typeof window !== 'undefined' && window.AvatarManager) || AvatarManager || null;
          const myAvatarId = avMgr && typeof avMgr.getSelectedAvatarId === 'function' ? avMgr.getSelectedAvatarId(s) : 'jack';
          const helloPayload = { type: 'hello', name: myName, avatarId: myAvatarId };
          if (myAvatarId === 'custom' && avMgr && typeof avMgr.getCustomAvatarData === 'function') {
            const dataUrl = avMgr.getCustomAvatarData(s);
            if (dataUrl) helloPayload.customAvatarData = dataUrl;
          }
          conn.send(helloPayload);
          setNetStatus('Connected to room ' + code + '. Waiting for seat…', 'wait');
        });
        conn.on('data', (msg) => {
          if (!msg || typeof msg !== 'object') return;
          if (net) net.lastHostSeen = Date.now();

          if (msg.type === 'host_offline' || (msg.type === 'leave' && msg.seatId === 'p1')) {
            NetworkManager.handleHostDisconnect(context);
            return;
          } else if (msg.type === 'player_offline') {
            if (msg.seatId && settings && settings.seats) {
              const s = settings.seats.find(x => x && x.id === msg.seatId);
              if (s) s.online = false;
            }
            if (Array.isArray(msg.onlineSeats)) {
              net.onlineSeats = msg.onlineSeats;
            } else if (net && net.onlineSeats && msg.seatId) {
              net.onlineSeats = net.onlineSeats.filter(id => id !== msg.seatId);
            }
            const renderScores = context.renderScores || (typeof window !== 'undefined' ? window.renderScores : null);
            if (typeof renderScores === 'function') renderScores(false);
            const render = context.render || (typeof window !== 'undefined' ? window.render : null);
            if (typeof render === 'function') render();
            const setNetStatus = context.setNetStatus || (typeof window !== 'undefined' ? window.setNetStatus : () => {});
            const offlineName = NetworkManager.sanitizePlayerName(msg.name, msg.seatId ? msg.seatId.toUpperCase() : 'Player');
            setNetStatus(`${offlineName} went offline.`, 'wait');
            return;
          } else if (msg.type === 'ping') {
            try { conn.send({ type: 'pong', seatId: net.mySeatId }); } catch (e) {}
            return;
          } else if (msg.type === 'pong') {
            return;
          }

          const updateNetRoster = context.updateNetRoster || (typeof window !== 'undefined' ? window.updateNetRoster : NetworkManager.updateNetRoster);
          const applyRemoteState = context.applyRemoteState || (typeof window !== 'undefined' ? window.applyRemoteState : NetworkManager.applyRemoteState);

          if (msg.type === 'welcome') {
            net.mySeatId = msg.seatId;
            net.ready = true;
            NetworkManager.startHeartbeat(context);
            NetworkManager.initUnloadHandler(context);
            if (Array.isArray(msg.onlineSeats)) {
              net.onlineSeats = msg.onlineSeats;
            }
            if (settings) {
              if (Array.isArray(msg.seats)) {
                for (const s of msg.seats) {
                  if (s && s.name) {
                    s.name = NetworkManager.sanitizePlayerName(s.name, `Player ${(s.id || '').slice(1)}`);
                  }
                }
                settings.seats = msg.seats;
              }
              if (msg.playerCount) settings.playerCount = msg.playerCount;
              if (settings.seats) {
                const mySeatObj = settings.seats.find(s => s && s.id === msg.seatId);
                if (mySeatObj) {
                  mySeatObj.name = NetworkManager.sanitizePlayerName(myName, `Player ${(msg.seatId || '').slice(1)}`);
                  mySeatObj.type = 'human';
                  mySeatObj.online = true;
                }
                const hostSeatObj = settings.seats.find(s => s && s.id === 'p1');
                if (hostSeatObj) {
                  hostSeatObj.online = true;
                }
              }
            }
            if (typeof window !== 'undefined') window._currentRoster = null;
            if (Array.isArray(msg.seats)) {
              const avMgr = context.AvatarManager || (typeof window !== 'undefined' && window.AvatarManager) || AvatarManager || null;
              const BoardRenderer = context.BoardRenderer || (typeof window !== 'undefined' ? window.BoardRenderer : null);
              for (const sObj of msg.seats) {
                if (sObj && sObj.id && sObj.id !== net.mySeatId) {
                  if (sObj.avatar === 'custom' && sObj.customAvatarData && avMgr && typeof avMgr.registerRemoteCustomAvatar === 'function') {
                    avMgr.registerRemoteCustomAvatar(sObj.id, sObj.customAvatarData, sObj.name || `Player ${sObj.id.slice(1)}`);
                  }
                  if (BoardRenderer && typeof BoardRenderer.updatePegAvatarPattern === 'function') {
                    const patId = (sObj.avatar === 'custom') ? ('custom_' + sObj.id) : (sObj.avatar || 'jack');
                    BoardRenderer.updatePegAvatarPattern(sObj.id, patId);
                  }
                }
              }
              const bRenderer3D = context.boardRenderer3D || (typeof window !== 'undefined' ? (window.boardRenderer3D || window._currentRenderer3D) : null);
              if (bRenderer3D && typeof bRenderer3D.updatePegAvatars === 'function') {
                bRenderer3D.updatePegAvatars();
              }
            }
            setNetStatus(`Connected as ${myName} (${msg.seatId.toUpperCase()})! Waiting for host to start…`, 'on');
            if (typeof updateNetRoster === 'function') updateNetRoster(msg.roster);
          } else if (msg.type === 'lobby') {
            if (Array.isArray(msg.onlineSeats)) {
              net.onlineSeats = msg.onlineSeats;
            }
            if (settings) {
              if (Array.isArray(msg.seats)) {
                for (const s of msg.seats) {
                  if (s && s.name) {
                    s.name = NetworkManager.sanitizePlayerName(s.name, `Player ${(s.id || '').slice(1)}`);
                  }
                }
                settings.seats = msg.seats;
              }
              if (msg.playerCount) settings.playerCount = msg.playerCount;
              if (settings.seats && net.mySeatId) {
                const mySeatObj = settings.seats.find(s => s && s.id === net.mySeatId);
                if (mySeatObj) {
                  mySeatObj.name = NetworkManager.sanitizePlayerName(myName, `Player ${(net.mySeatId || '').slice(1)}`);
                  mySeatObj.type = 'human';
                  mySeatObj.online = true;
                }
                const hostSeatObj = settings.seats.find(s => s && s.id === 'p1');
                if (hostSeatObj) {
                  hostSeatObj.online = true;
                }
              }
            }
            if (typeof window !== 'undefined') window._currentRoster = null;
            if (Array.isArray(msg.seats)) {
              const avMgr = context.AvatarManager || (typeof window !== 'undefined' && window.AvatarManager) || AvatarManager || null;
              const BoardRenderer = context.BoardRenderer || (typeof window !== 'undefined' ? window.BoardRenderer : null);
              for (const sObj of msg.seats) {
                if (sObj && sObj.id && sObj.id !== net.mySeatId) {
                  if (sObj.avatar === 'custom' && sObj.customAvatarData && avMgr && typeof avMgr.registerRemoteCustomAvatar === 'function') {
                    avMgr.registerRemoteCustomAvatar(sObj.id, sObj.customAvatarData, sObj.name || `Player ${sObj.id.slice(1)}`);
                  }
                  if (BoardRenderer && typeof BoardRenderer.updatePegAvatarPattern === 'function') {
                    const patId = (sObj.avatar === 'custom') ? ('custom_' + sObj.id) : (sObj.avatar || 'jack');
                    BoardRenderer.updatePegAvatarPattern(sObj.id, patId);
                  }
                }
              }
              const bRenderer3D = context.boardRenderer3D || (typeof window !== 'undefined' ? (window.boardRenderer3D || window._currentRenderer3D) : null);
              if (bRenderer3D && typeof bRenderer3D.updatePegAvatars === 'function') {
                bRenderer3D.updatePegAvatars();
              }
            }
            if (typeof updateNetRoster === 'function') updateNetRoster(msg.roster);
          } else if (msg.type === 'state') {
            if (typeof applyRemoteState === 'function') applyRemoteState(msg);
          } else if (msg.type === 'cam_state') {
            try {
              const camMgr = (context.CameraManager && context.CameraManager.getInstance ? context.CameraManager.getInstance() : CameraManager.getInstance());
              if (camMgr) camMgr.handleCamStateMessage(msg, context);
            } catch (e) {
              console.warn('[CameraManager] Guest cam_state error:', e);
            }
          } else if (msg.type === 'chat') {
            try {
              const chatMgr = (context.ChatManager && context.ChatManager.getInstance ? context.ChatManager.getInstance() : ChatManager.getInstance());
              if (chatMgr) chatMgr.handleIncomingMessage(msg, context);
            } catch (e) {
              console.warn('[ChatManager] Guest chat message error:', e);
            }
          } else if (msg.type === 'reaction') {
            try {
              const sfx = context.sfx || (typeof window !== 'undefined' ? window.sfx : null);
              ReactionManager.handleRemoteReaction(msg, { sfx });
            } catch (e) {
              console.warn('[ReactionManager] Guest reaction error:', e);
            }
          } else if (msg.type === 'rematch') {
            try {
              const playAgain = context.playAgain || (typeof window !== 'undefined' ? window.playAgain : null);
              if (typeof playAgain === 'function') playAgain();
            } catch (e) {}
          } else if (msg.type === 'tournament_update') {
            try {
              if (msg.tournament) {
                const TournamentManager = context.TournamentManager || (typeof window !== 'undefined' ? window.TournamentManager : null);
                if (TournamentManager) {
                  TournamentManager.saveTournamentState(msg.tournament);
                }
                const TournamentModal = context.TournamentModal || (typeof window !== 'undefined' ? window.TournamentModal : null);
                if (TournamentModal) {
                  const overlay = document.getElementById(TournamentModal.OVERLAY_ID);
                  if (overlay && overlay.style.display !== 'none') {
                    TournamentModal.render(context);
                  }
                }
              }
            } catch (e) {
              console.warn('[Tournament] Guest tournament update error:', e);
            }
          } else if (msg.type === 'avatar_update') {
            try {
              NetworkManager.handleAvatarUpdate(msg, context);
            } catch (e) {
              console.warn('[AvatarManager] Guest avatar_update error:', e);
            }
          }
        });

        const triggerHostDisconnect = () => {
          NetworkManager.handleHostDisconnect(context);
        };

        conn.on('close', triggerHostDisconnect);
        conn.on('error', (err) => {
          triggerHostDisconnect();
        });

        if (conn.peerConnection) {
          try {
            conn.peerConnection.addEventListener('connectionstatechange', () => {
              const st = conn.peerConnection.connectionState;
              if (st === 'disconnected' || st === 'failed' || st === 'closed') {
                triggerHostDisconnect();
              }
            });
            conn.peerConnection.addEventListener('iceconnectionstatechange', () => {
              const st = conn.peerConnection.iceConnectionState;
              if (st === 'disconnected' || st === 'failed' || st === 'closed') {
                triggerHostDisconnect();
              }
            });
          } catch (e) {}
        }
      });
      net.peer.on('error', (err) => {
        hideWait();
        if (err && err.type === 'peer-unavailable') {
          setNetStatus('Room "' + code + '" not found. Ensure host is waiting.', '');
        } else {
          setNetStatus('Join error: ' + (err ? err.type || err : 'unknown'), '');
        }
      });
    }
  }

  /**

   * Parses URL room parameters/hash and initiates auto-joining flow.
   * @param {Object} [context]
   */
  static initAutoJoin(context = {}) {
    let joinCode = '';
    const win = context.window || (typeof window !== 'undefined' ? window : null);
    if (!win) return;

    try {
      const params = new URLSearchParams(win.location.search);
      joinCode = params.get('join') || params.get('room') || '';
      if (!joinCode && win.location.hash) {
        const hash = win.location.hash.replace(/^#/, '');
        const hashParams = new URLSearchParams(hash.includes('?') ? hash.split('?')[1] : hash);
        joinCode = hashParams.get('join') || hashParams.get('room') || '';
        if (!joinCode && /^[A-Z0-9]{4,8}$/i.test(hash)) {
          joinCode = hash;
        }
      }
    } catch (e) {}

    if (!joinCode) return;
    joinCode = joinCode.trim().toUpperCase();

    const doc = context.document || win.document;
    const settings = context.settings || win.settings;
    if (doc) {
      const pCount = doc.getElementById('optPlayerCount');
      if (pCount) {
        pCount.value = '2';
        pCount.dispatchEvent(new Event('change'));
      }
    }
    if (settings) {
      settings.playerCount = 2;
      settings.gameMode = 'indiv';
    }

    if (doc) {
      const oppSelect = doc.getElementById('optOpp');
      if (oppSelect) {
        oppSelect.value = 'online';
        oppSelect.dispatchEvent(new Event('change'));
      }
      const roomInput = doc.getElementById('optRoom');
      if (roomInput) {
        roomInput.value = joinCode;
      }
      const netPanel = doc.getElementById('netPanel');
      if (netPanel) {
        netPanel.classList.remove('hidden');
      }
      const menuOptions = doc.getElementById('menuOptions');
      if (menuOptions && menuOptions.classList.contains('hidden')) {
        menuOptions.classList.remove('hidden');
        const btnSettingsMenu = doc.getElementById('btnSettingsMenu');
        if (btnSettingsMenu) btnSettingsMenu.classList.add('is-active');
      }
    }
    if (settings) {
      settings.opp = 'online';
    }

    const setNetStatus = context.setNetStatus || win.setNetStatus || (() => {});
    const showWait = context.showWait || win.showWait || (() => {});
    const hideWait = context.hideWait || win.hideWait || (() => {});
    const joinRoom = context.joinRoom || win.joinRoom || NetworkManager.joinRoom;
    const s = getStorage(context);

    const savedName = (doc && doc.getElementById('optPlayerName') && doc.getElementById('optPlayerName').value.trim())
      || (s ? (s.getItem('cc_player_name') || '').trim() : '');

    if (!savedName) {
      setNetStatus('Room invite detected. Please enter your name to join…', 'wait');
      NetworkManager.promptPlayerName(joinCode, (enteredName) => {
        if (doc && doc.getElementById('optPlayerName')) doc.getElementById('optPlayerName').value = enteredName;
        try { if (s) s.setItem('cc_player_name', enteredName); } catch (e) {}
        joinRoom(joinCode, { ...context, playerName: enteredName });
      }, context);
      return;
    }

    if (doc && doc.getElementById('optPlayerName') && !doc.getElementById('optPlayerName').value) {
      doc.getElementById('optPlayerName').value = savedName;
    }

    setNetStatus('Auto-joining room ' + joinCode + '…', 'wait');
    showWait('Connecting to host room ' + joinCode + '…');

    function attemptJoin(retries = 25) {
      const PeerClass = context.PeerClass || win.Peer;
      if (typeof PeerClass === 'undefined') {
        if (retries > 0) {
          setTimeout(() => attemptJoin(retries - 1), 150);
        } else {
          setNetStatus('PeerJS failed to load. Please refresh and serve over http/https.', '');
          hideWait();
        }
        return;
      }
      joinRoom(joinCode);
    }
    setTimeout(attemptJoin, 150);
  }

  updateNetRoster(remoteRoster, context) {
    return NetworkManager.updateNetRoster(remoteRoster, { net: this, ...context });
  }

  broadcastLobbyUpdate(context) {
    return NetworkManager.broadcastLobbyUpdate({ net: this, ...context });
  }

  netSend(msg, targetConn, context) {
    return NetworkManager.netSend(msg, targetConn, { net: this, ...context });
  }

  netPush(extra, context) {
    return NetworkManager.netPush(extra, { net: this, ...context });
  }

  applyRemoteState(payload, context) {
    return NetworkManager.applyRemoteState(payload, { net: this, ...context });
  }

  hostRoom(context) {
    return NetworkManager.hostRoom({ net: this, ...context });
  }

  joinRoom(code, context) {
    return NetworkManager.joinRoom(code, { net: this, ...context });
  }

  /**
   * Sets up net panel visibility, host/join buttons, copy room link, and quick mode pills.
   * @param {Object} [context]
   */
  static setupNetUi(context = {}) {
    if (typeof document === 'undefined') return;
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const net = context.net || (typeof window !== 'undefined' ? window.net : {});
    const showHostInviteLink = context.showHostInviteLink || (typeof window !== 'undefined' ? window.showHostInviteLink : () => {});
    const hostRoom = context.hostRoom || (typeof window !== 'undefined' ? window.hostRoom : () => {});
    const joinRoom = context.joinRoom || (typeof window !== 'undefined' ? window.joinRoom : () => {});
    const copyRoomInviteLink = context.copyRoomInviteLink || (typeof window !== 'undefined' ? window.copyRoomInviteLink : () => {});
    const startGameOnline = context.startGameOnline || (typeof window !== 'undefined' ? window.startGameOnline : () => {});
    const syncPlayerCountUi = context.syncPlayerCountUi || (typeof window !== 'undefined' ? window.syncPlayerCountUi : () => {});
    const getAppBrand = context.getAppBrand || (typeof window !== 'undefined' ? window.getAppBrand : () => 'crazy');
    const GameOptionsEngine = context.GameOptionsEngine || (typeof window !== 'undefined' ? window.GameOptionsEngine : null);

    const opp = document.getElementById('optOpp');
    const panel = document.getElementById('netPanel');
    const sync = () => {
      if (!panel || !opp) return;
      panel.classList.toggle('hidden', opp.value !== 'online');
      if (opp.value === 'online' && settings.netRole === 'host' && net.room) {
        showHostInviteLink(net.room);
      }
    };
    if (opp) opp.addEventListener('change', sync);
    sync();

    const optPlayerName = document.getElementById('optPlayerName');
    const s = getStorage(context);
    if (optPlayerName) {
      try {
        const saved = s ? s.getItem('cc_player_name') : null;
        if (saved && !optPlayerName.value) optPlayerName.value = saved;
      } catch (e) {}
      optPlayerName.addEventListener('input', () => {
        try {
          const val = optPlayerName.value.trim();
          if (val && s) s.setItem('cc_player_name', val);
        } catch (e) {}
      });
    }

    const bh = document.getElementById('btnHost');
    const bj = document.getElementById('btnJoin');
    const bc = document.getElementById('btnCopyRoomLink');
    const bcb = document.getElementById('btnCopyRoomLinkBar');
    const bStart = document.getElementById('btnStartOnlineGame');
    const bShareInvite = document.getElementById('btnShareInvite');
    const bShareWhatsApp = document.getElementById('btnShareWhatsApp');
    const bShareEmail = document.getElementById('btnShareEmail');
    const bShareMore = document.getElementById('btnShareMore');
    if (bh) bh.onclick = (e) => { e.preventDefault(); hostRoom(); };
    if (bj) bj.onclick = (e) => { e.preventDefault(); joinRoom(); };
    if (bc) bc.onclick = (e) => { e.preventDefault(); copyRoomInviteLink(bc); };
    if (bcb) bcb.onclick = (e) => { e.preventDefault(); copyRoomInviteLink(bcb); };
    if (bShareInvite) bShareInvite.onclick = (e) => { e.preventDefault(); NetworkManager.shareRoomInvite(context); };
    if (bShareWhatsApp) bShareWhatsApp.onclick = (e) => { e.preventDefault(); NetworkManager.shareRoomWhatsApp(context); };
    if (bShareEmail) bShareEmail.onclick = (e) => { e.preventDefault(); NetworkManager.shareRoomEmail(context); };
    if (bShareMore) bShareMore.onclick = (e) => { e.preventDefault(); (typeof window !== 'undefined' && window.showShareModal ? window.showShareModal() : NetworkManager.shareRoomInvite(context)); };
    if (bStart) bStart.onclick = (e) => { e.preventDefault(); startGameOnline(); };

    if (typeof window !== 'undefined') {
      window.shareRoomInvite = (ctx) => NetworkManager.shareRoomInvite(ctx || context);
      window.shareRoomWhatsApp = (ctx) => NetworkManager.shareRoomWhatsApp(ctx || context);
      window.shareRoomEmail = (ctx) => NetworkManager.shareRoomEmail(ctx || context);
    }

    // Game Mode Quick-Toggle Pills
    const modePills = document.querySelectorAll('.game-mode-pill');
    modePills.forEach(btn => {
      btn.onclick = (e) => {
        e.preventDefault();
        const mode = btn.dataset.mode;
        const b = getAppBrand();
        const allowCpu = GameOptionsEngine && typeof GameOptionsEngine.isCpuAllowed === 'function'
          ? GameOptionsEngine.isCpuAllowed(b)
          : !['pints', 'adult'].includes(b);
        if (!allowCpu && mode === 'cpu') return;
        if (opp) {
          opp.value = mode;
          const aiRow = document.getElementById('optAiProfileRow');
          if (aiRow) {
            aiRow.classList.toggle('hidden', mode !== 'cpu');
            aiRow.style.display = (mode === 'cpu' && allowCpu) ? '' : 'none';
          }
          syncPlayerCountUi();
        }
      };
    });

    // Table Size Quick-Buttons inside Online Room panel
    const sizeBtns = document.querySelectorAll('.net-size-btn');
    const pCountSel = document.getElementById('optPlayerCount');
    sizeBtns.forEach(btn => {
      btn.onclick = (e) => {
        e.preventDefault();
        const countVal = btn.dataset.count;
        if (pCountSel) {
          pCountSel.value = countVal;
          syncPlayerCountUi();
        }
      };
    });

    // Listen to Seat Type changes in seatConfigPanel
    ['optSeat2Type','optSeat3Type','optSeat4Type','optSeat5Type','optSeat6Type','optSeat7Type','optSeat8Type'].forEach(id => {
      const sel = document.getElementById(id);
      if (sel) {
        sel.addEventListener('change', () => {
          if (sel.value === 'online') {
            if (opp) {
              opp.value = 'online';
              syncPlayerCountUi();
            }
          }
        });
      }
    });
  }

  /**
   * Starts periodic heartbeat ping/pong to detect dropped peer connections within 5-7s.
   * @param {Object} [context]
   */
  static startHeartbeat(context = {}) {
    if (typeof window === 'undefined') return;
    if (window._ccHeartbeatTimer) clearInterval(window._ccHeartbeatTimer);

    window._ccHeartbeatTimer = setInterval(() => {
      const net = context.net || window.net;
      const settings = context.settings || window.settings;
      if (!net || (settings && settings.opp !== 'online')) return;

      const now = Date.now();
      const isHost = Boolean((settings && settings.netRole === 'host') || net.isHost || net.role === 'host');

      if (isHost) {
        if (!net.clients) return;
        for (const [, client] of net.clients.entries()) {
          if (!client || !client.conn) continue;
          if (client.lastSeen && (now - client.lastSeen > 7000)) {
            console.warn(`[NetworkManager] Client ${client.seatId} heartbeat timeout (>7s), marking offline.`);
            const handleClientDisconnect = context.handleClientDisconnect || window.handleClientDisconnect || NetworkManager.handleClientDisconnect;
            if (typeof handleClientDisconnect === 'function') {
              handleClientDisconnect(client.conn, context);
            }
            continue;
          }
          if (client.conn.open) {
            try { client.conn.send({ type: 'ping' }); } catch (e) {}
          }
        }
      } else {
        if (!net.conn) return;
        if (net.ready && net.lastHostSeen && (now - net.lastHostSeen > 7000)) {
          console.warn('[NetworkManager] Host heartbeat timeout (>7s), marking host offline.');
          NetworkManager.handleHostDisconnect(context);
          return;
        }
        if (net.conn.open) {
          try { net.conn.send({ type: 'pong', seatId: net.mySeatId }); } catch (e) {}
        }
      }
    }, 2500);
  }

  /**
   * Attaches page unload listeners to instantly notify peers on window close or tab navigation.
   * @param {Object} [context]
   */
  static initUnloadHandler(context = {}) {
    if (typeof window === 'undefined' || window._ccUnloadInitialized) return;
    window._ccUnloadInitialized = true;

    const onLeave = () => {
      const net = context.net || window.net;
      const settings = context.settings || window.settings;
      if (!net) return;

      const isHost = Boolean((settings && settings.netRole === 'host') || net.isHost || net.role === 'host');
      if (isHost && net.clients) {
        for (const c of net.clients.values()) {
          if (c && c.conn && c.conn.open) {
            try {
              c.conn.send({ type: 'host_offline', seatId: 'p1' });
              c.conn.close();
            } catch (e) {}
          }
        }
      } else if (net.conn && net.conn.open) {
        try {
          net.conn.send({ type: 'client_leave', seatId: net.mySeatId || 'p2' });
          net.conn.close();
        } catch (e) {}
      }
      if (net.peer && typeof net.peer.destroy === 'function') {
        try { net.peer.destroy(); } catch (e) {}
      }
    };

    window.addEventListener('beforeunload', onLeave);
    window.addEventListener('pagehide', onLeave);
  }

  /**
   * Handles incoming avatar_update message (both host and guest).
   * @param {Object} msg - { type: 'avatar_update', seatId, avatarId, customAvatarData, name }
   * @param {Object} [context]
   */
  static handleAvatarUpdate(msg, context = {}) {
    if (!msg || !msg.seatId) return;
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
    const avMgr = context.AvatarManager || (typeof window !== 'undefined' && window.AvatarManager) || AvatarManager || null;

    // Register remote custom avatar if dataUrl is provided
    if (msg.avatarId === 'custom' && msg.customAvatarData && avMgr && typeof avMgr.registerRemoteCustomAvatar === 'function') {
      const seatObj = settings && Array.isArray(settings.seats) ? settings.seats.find(s => s && s.id === msg.seatId) : null;
      const fallbackPeerName = `Player ${msg.seatId.slice(1)}`;
      const peerName = NetworkManager.sanitizePlayerName((seatObj && seatObj.name) || msg.name, fallbackPeerName);
      avMgr.registerRemoteCustomAvatar(msg.seatId, msg.customAvatarData, peerName);
    }

    // Update settings.seats if present
    if (settings && Array.isArray(settings.seats)) {
      const seatObj = settings.seats.find(s => s && s.id === msg.seatId);
      if (seatObj) {
        seatObj.avatar = msg.avatarId;
        if (msg.customAvatarData) seatObj.customAvatarData = msg.customAvatarData;
      }
    }

    // Update 2D peg avatar pattern
    const BoardRenderer = context.BoardRenderer || (typeof window !== 'undefined' ? window.BoardRenderer : null);
    if (BoardRenderer && typeof BoardRenderer.updatePegAvatarPattern === 'function') {
      const targetPatternAvatarId = (msg.avatarId === 'custom') ? ('custom_' + msg.seatId) : msg.avatarId;
      BoardRenderer.updatePegAvatarPattern(msg.seatId, targetPatternAvatarId);
    }

    // Update 3D pegs if active
    const bRenderer3D = context.boardRenderer3D || (typeof window !== 'undefined' ? (window.boardRenderer3D || window._currentRenderer3D) : null);
    if (bRenderer3D && typeof bRenderer3D.updatePegAvatars === 'function') {
      bRenderer3D.updatePegAvatars();
    }

    // Refresh lobby roster if modal open
    const updateNetRoster = context.updateNetRoster || (typeof window !== 'undefined' ? window.updateNetRoster : NetworkManager.updateNetRoster);
    if (typeof updateNetRoster === 'function') {
      if (typeof window !== 'undefined') window._currentRoster = null;
      updateNetRoster();
    }

    // Refresh UI / Scoreboard badges
    const renderScores = context.renderScores || (typeof window !== 'undefined' ? window.renderScores : null);
    if (typeof renderScores === 'function') renderScores(false);
  }

  setupNetUi(context) {
    return NetworkManager.setupNetUi(context);
  }
}
